import { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { RecruiterCockpit, RulesEngineView, IntegrationsView, AIGovernanceView, ComplianceSandboxView, InterviewSchedulerView, PublishJobsView, PublicJobsBoard, PublicJobDetail } from './CockpitComponents';
import { HiringManagerPortal } from './HiringManagerPortal';
import { MessageBoard, MessageBell } from './MessageBoard';
import { CandidateCompliancePack, EmployerCompliancePacks } from './CompliancePack';
import PricingPage from './Pricing';
import PaymentSuccessPage from './PaymentSuccess';
import TrustPage from './Trust';
import AnnualUpsellBanner from './AnnualUpsellBanner';
import { CandidateOffersBanner, AnalyticsDashboard } from './AnalyticsAndOffers';
import { CandidateMutualInterestBanner } from './MutualInterest';
import { ReferenceLibraryPanel, RightToWorkPanel } from './ReferencesAndRTW';
import { CandidatePackPanel } from './RequirementManifest';
import TrustDataMinimisationPage from './TrustDataMinimisation';
import ComplianceCustomerPortal from './ComplianceCustomer';
import ComplianceAdminPortal from './ComplianceAdmin';
import { PMProjectList, PMProjectDetail } from './ImplementationPM';
import PMInbox from './PMInbox';
import VaultPage from './shell/VaultPage';
import AppShellRuntime from './shell/AppShell';
import RecruiterCandidateActions from './shell/RecruiterCandidateActions';
import WalkthroughInbox from './shell/WalkthroughInbox';
import RecentWalkthroughViewers from './shell/RecentWalkthroughViewers';
import RecruiterHQ from './RecruiterHQ';
import RecruiterWidgetDashboard from './RecruiterWidgetDashboard';
import CompaniesDirectory from './CompaniesDirectory';
import InvestorRoom from './InvestorRoom';
import InvestorAdmin from './InvestorAdmin';
import AIPreferencesPage from './AIPreferences';
import AdminAIControlsPage from './AdminAIControls';
import ComplianceHubPage from './ComplianceHub';
import AdminDPAQueuePage from './AdminDPAQueue';
import { logger } from './utils/logger';
import { registerPasskey, signInWithPasskey, isWebAuthnSupported } from './lib/webauthnClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Smart 401 handler: try refresh first, then redirect to login
let _refreshPromise = null;
const _tryRefresh = () => {
  if (!_refreshPromise) {
    _refreshPromise = axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {})
      .finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const cfg = error.config || {};
    const url = cfg.url || '';
    const isAuthEndpoint = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/register');
    if (status === 401 && !isAuthEndpoint && !cfg.__triedRefresh) {
      cfg.__triedRefresh = true;
      try {
        await _tryRefresh();
        return axios(cfg);  // retry original request with refreshed cookie
      } catch (e) {
        // Refresh failed - session truly expired
        const path = window.location.pathname + window.location.search;
        if (!path.startsWith('/login') && !path.startsWith('/register') && !path.startsWith('/jobs/')) {
          window.location.replace(`/login?redirect=${encodeURIComponent(path)}`);
        }
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);
export { useAuth };

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password, mfaCode = null) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, mfa_code: mfaCode });
    // Login response is the bare user object — re-fetch /auth/me to pick up
    // computed fields (passkey_nudge, etc).
    try {
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      return me.data;
    } catch (e) {
      setUser(data);
      return data;
    }
  }, []);

  const register = useCallback(async (userData) => {
    const { data } = await axios.post(`${API}/auth/register`, userData);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const contextValue = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth }),
    [user, loading, login, register, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Owner can access admin routes
  if (role && user.role !== role) {
    if (!(role === 'admin' && user.role === 'owner')) {
      return <Navigate to="/" />;
    }
  }

  return (
    <>
      <PasskeyNudgeBanner />
      {children}
    </>
  );
}

function PasskeyNudgeBanner() {
  const { user, checkAuth } = useAuth();
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();
  const nudge = user?.passkey_nudge;
  const supported = isWebAuthnSupported();

  if (!nudge || !nudge.should_show || hidden || !supported) return null;

  const dismiss = async () => {
    setHidden(true);
    try {
      await axios.post(`${API}/me/passkey-nudge/dismiss`);
      checkAuth();
    } catch (e) { /* silent — already hidden client-side */ }
  };

  return (
    <div
      className="bg-emerald-50 border-b border-emerald-200 px-4 py-2"
      data-testid="passkey-nudge-banner"
      role="status"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-emerald-900 min-w-0">
          <span aria-hidden className="text-base">🔐</span>
          <span className="truncate">
            <strong>Sign in faster next time.</strong> Add a passkey — biometric or security-key sign-in, no password required.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => { setHidden(true); navigate('/security/passkeys'); }}
            className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-medium"
            data-testid="passkey-nudge-add"
          >
            Add a passkey
          </button>
          <button
            onClick={dismiss}
            className="text-xs text-emerald-700 hover:text-emerald-900 px-2"
            data-testid="passkey-nudge-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

const LANDING_PERSONAS = [
  {
    id: 'candidate',
    label: 'For candidates',
    eyebrow: 'Built for the person being hired',
    headline: 'Build it once. Carry it for life.',
    body: 'Every job application asks for the same things — your work history, education, salary, why you’re leaving. You type them into a fresh form every time, and the data goes into someone else’s database. Vault Hire is the first place that record belongs to you, and stays with you.',
    bullets: [
      { title: 'One profile you build over a career', text: 'Maintain your work history, CV, education and skills in one place. Update it once; everyone you share with sees the current version.' },
      { title: 'Share as a link, not a form', text: 'Issue a time-bound link to any recruiter or employer. They see the version of you you’ve approved — no copy-pasting into yet another portal.' },
      { title: 'A profile you actually sign off', text: 'When AI drafts a summary of you from your verified data, you read it and approve it before any recruiter sees it. No surprises about what’s being said.' },
      { title: 'References & ID, ready for offer stage', text: 'Pre-arrange your referees and store your right-to-work documents once. When an offer comes, you authorise the release — you don’t scramble to find old managers’ emails.' },
    ],
    cta: { to: '/register', label: 'Start your vault — free for life' },
    altCta: { to: '/login', label: 'I already have a vault' },
  },
  {
    id: 'recruiter',
    label: 'For recruiters',
    eyebrow: 'Banking-grade Vault Hub',
    headline: 'Stop chasing references. Start running pipelines.',
    body: 'Verified candidates, structured scorecards, on-demand vault access, AI overviews tied to live requisitions, and full FCA / SMCR evidence — all in one Vault Hub that your hiring managers actually want to use.',
    bullets: [
      { title: 'Request a vault, not a CV', text: 'Ask candidates for time-limited, section-scoped access. Approval issues a 24-hour link tracked end-to-end.' },
      { title: 'Push an AI overview to a job', text: 'Attach a candidate’s approved AI profile to a requisition — every hiring-manager collaborator gets notified in-app.' },
      { title: 'Reference reuse', text: 'Candidates carry verified references between roles. Faster fills, lower TA cost, zero re-pinging referees.' },
      { title: 'Compliance pack ready', text: 'Right-to-Work, regulatory references, scorecards, offer audit — all aligned to FCA SMCR + UK GDPR.' },
    ],
    cta: { to: '/register', label: 'Open your Vault Hub' },
    altCta: { to: '/pricing', label: 'See pricing' },
  },
  {
    id: 'hiring_manager',
    label: 'For hiring managers',
    eyebrow: 'Calm. Structured. On the record.',
    headline: 'Decide with confidence — and a defensible audit trail.',
    body: 'Score every candidate against the same competencies. Sign off references and offers digitally. Receive an AI overview only when it is grounded in verified data, never in hype.',
    bullets: [
      { title: 'Structured scorecards', text: 'Default templates per role (trader, credit analyst, compliance, engineer) — or build your own in five minutes.' },
      { title: 'AI overviews, not AI guesses', text: 'Every AI overview is versioned, candidate-approved, and bound to a verifiable record state. No hallucinated qualifications.' },
      { title: 'Decision log', text: 'Material decisions, options considered, rationale and reversal cost — all captured for the regulator.' },
    ],
    cta: { to: '/register', label: 'Join your team’s Vault Hub' },
    altCta: { to: '/trust', label: 'Why trust this?' },
  },
  {
    id: 'bank',
    label: 'For banks & RegOps',
    eyebrow: 'Self-serve compliance — and a real cradle-to-grave PM tool',
    headline: 'Run hiring as a regulated process, end-to-end.',
    body: 'From signed sales contract to go-live to lessons-learned — every artefact is tamper-evident, every change is signed, and every AI inference is logged for the regulator.',
    bullets: [
      { title: 'AI Compliance Response Platform', text: 'Answer your customers’ security questionnaires in days, not quarters — NDA-gated, AI-drafted, human-verified.' },
      { title: 'Implementation PM tool', text: 'Scope, RACI, sub-tasks, dependencies, tollgates, dual sign-off, anonymised lessons-learned — built for banking onboarding.' },
      { title: 'EU AI Act + UK GDPR ready', text: 'AI inference register, automated-decision opt-out honoured at the request boundary, data-minimisation by default. DPIA template included.' },
    ],
    cta: { to: '/admin/compliance', label: 'See the compliance portal' },
    altCta: { to: '/trust/data-minimisation', label: 'Read the privacy spec' },
  },
];

function LandingTopBar() {
  return (
    <header className="border-b border-[var(--vh-border)] bg-[var(--vh-bg-base)]/85 backdrop-blur sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)]" data-testid="landing-logo">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--vh-text-secondary)]">
          <Link to="/jobs" className="hover:text-[var(--vh-brand-primary)]" data-testid="landing-nav-jobs">Jobs board</Link>
          <Link to="/companies" className="hover:text-[var(--vh-brand-primary)]" data-testid="landing-nav-companies">Companies</Link>
          <Link to="/trust" className="hover:text-[var(--vh-brand-primary)]" data-testid="landing-nav-trust">Trust</Link>
          <Link to="/pricing" className="hover:text-[var(--vh-brand-primary)]" data-testid="landing-nav-pricing">Pricing</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 rounded-full text-sm text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)]" data-testid="landing-nav-login">Sign in</Link>
          <Link to="/register" className="px-4 py-2 rounded-full text-sm bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]" data-testid="landing-nav-register">Get started</Link>
        </div>
      </div>
    </header>
  );
}

function LandingPersonaPanel({ persona }) {
  return (
    <div className="grid lg:grid-cols-5 gap-10 items-start" data-testid={`landing-persona-${persona.id}`}>
      <div className="lg:col-span-2">
        <p className="text-xs uppercase tracking-[0.18em] text-[var(--vh-accent)] mb-3">{persona.eyebrow}</p>
        <h2 className="font-display text-3xl md:text-4xl leading-tight text-[var(--vh-text-primary)] mb-5">{persona.headline}</h2>
        <p className="text-base text-[var(--vh-text-secondary)] leading-relaxed mb-7 max-w-md">{persona.body}</p>
        <div className="flex flex-wrap gap-3">
          <Link to={persona.cta.to} className="px-5 py-3 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-medium" data-testid={`landing-cta-${persona.id}`}>
            {persona.cta.label} →
          </Link>
          <Link to={persona.altCta.to} className="px-5 py-3 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] text-sm font-medium" data-testid={`landing-altcta-${persona.id}`}>
            {persona.altCta.label}
          </Link>
        </div>
      </div>
      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
        {persona.bullets.map((b, idx) => (
          <div key={b.title} className="p-5 rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] shadow-sm" data-testid={`landing-bullet-${persona.id}-${idx}`}>
            <div className="w-8 h-8 rounded-full bg-[var(--vh-bg-muted)] text-[var(--vh-brand-primary)] flex items-center justify-center font-display text-sm mb-3">{idx + 1}</div>
            <h3 className="font-display text-lg text-[var(--vh-text-primary)] mb-1.5">{b.title}</h3>
            <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">{b.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('candidate');

  useEffect(() => {
    if (user) {
      if (user.role === 'candidate') navigate('/candidate/dashboard');
      else if (user.role === 'employer') navigate('/employer/dashboard');
      else if (user.role === 'hiring_manager') navigate('/hiring-manager');
      else if (user.role === 'admin' || user.role === 'owner') navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  const persona = LANDING_PERSONAS.find(p => p.id === active);

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)]">
      <LandingTopBar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-4" data-testid="landing-eyebrow">Built for the person being hired</p>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.04] mb-6 text-[var(--vh-text-primary)]" data-testid="landing-title">
              We can’t change the past.<br />
              <span className="text-[var(--vh-brand-primary)]">We can change what comes next.</span>
            </h1>
            <p className="text-lg text-[var(--vh-text-secondary)] leading-relaxed max-w-2xl mb-5">
              You’ve made 10 job applications. At an average of 9 minutes each, that’s an hour and a half of your time — typing the same work history, the same education, the same reasons for leaving into 10 different portals. Your personal data is now scattered across as many databases. Keeping track of it is nigh on impossible.
            </p>
            <p className="text-lg text-[var(--vh-text-secondary)] leading-relaxed max-w-2xl mb-8">
              <strong>Meet Vault Hire.</strong> A single record of your career that you own — built once, refined over time, shared on your terms. Free for life.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="px-6 py-3 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] text-sm font-semibold shadow-sm" data-testid="landing-hero-register">
                Start your vault — free for life →
              </Link>
              <Link to="/login" className="px-6 py-3 rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] text-sm font-semibold" data-testid="landing-hero-login">
                Sign in
              </Link>
              <Link to="/jobs" className="px-6 py-3 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] text-sm font-medium" data-testid="landing-hero-jobs">
                Browse open jobs
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[var(--vh-text-muted)]">
              <span>✓ Build your profile once — keep it for life</span>
              <span>✓ Share as a link, not a form</span>
              <span>✓ See who’s accessed it, and when</span>
              <span>✓ Free for candidates. Always.</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="p-6 rounded-3xl bg-[var(--vh-brand-primary)] text-white shadow-xl" data-testid="landing-vault-card">
              <p className="text-xs uppercase tracking-widest text-white/70 mb-2">Your vault, at a glance</p>
              <div className="flex items-end gap-3 mb-6">
                <div className="font-display text-6xl leading-none">82<span className="text-2xl">%</span></div>
                <p className="text-sm text-white/80 mb-2">ready to share</p>
              </div>
              <ul className="space-y-2 text-sm">
                {[
                  { label: 'Work history', state: 'In your vault', filled: true },
                  { label: 'Education', state: 'In your vault', filled: true },
                  { label: 'Skills & qualifications', state: 'In your vault', filled: true },
                  { label: 'Approved CV summary', state: 'In your vault', filled: true },
                  { label: 'References (for offer stage)', state: 'Ready when needed', filled: false },
                ].map((item) => (
                  <li key={item.label} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${item.filled ? 'bg-[var(--vh-warning)]' : 'bg-[var(--vh-accent)]'}`} />
                    <span className="text-white/90">{item.label}</span>
                    <span className="ml-auto text-xs text-white/60">{item.state}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/60 mt-6">A live preview of a candidate vault. Yours starts empty — fill it once, reuse it everywhere.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pain → Fix (candidate-first) */}
      <section className="bg-[var(--vh-bg-surface)] py-20 border-y border-[var(--vh-border)]" data-testid="landing-pain-fix-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.18em] text-[var(--vh-accent)] mb-3">Why Vault Hire?</p>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] leading-tight">
              You’ve built your professional record a hundred times.<br />
              <span className="text-[var(--vh-brand-primary)]">This time, you keep it.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="landing-pain-fix-cards">
            {[
              {
                pain: 'You tell the same story, every time',
                pain_body: 'Same dates, same job titles, same "why are you leaving" — typed into a fresh portal for every application. None of it’s yours after you click submit.',
                fix: 'One profile. Build it once.',
                fix_body: 'Maintain your work history, education and CV in one vault you own. Share it as a link in 30 seconds, not a form in 30 minutes.',
              },
              {
                pain: 'Your professional record is scattered',
                pain_body: 'A CV on your laptop, a handful of ATS accounts you barely remember setting up, half-finished profiles on a few job sites, recruiter emails. No single source of truth that belongs to you.',
                fix: 'One source of truth — yours.',
                fix_body: 'A vault you keep refining across roles. Update it once; everyone you share with sees the current version. Yours for life, free.',
              },
              {
                pain: 'At offer stage, the scramble starts',
                pain_body: 'Once an offer arrives, the requests come in: references, right-to-work, salary slips, ID. Hunting for old managers’ emails. Repeating it for every offer.',
                fix: 'Already in your vault when it’s asked for.',
                fix_body: 'Pre-arrange referees and store your right-to-work and ID once. When an employer asks, you authorise the release — you don’t rebuild it from scratch.',
              },
            ].map((c, i) => (
              <div
                key={c.pain}
                className="p-6 rounded-2xl bg-[var(--vh-bg-base)] border border-[var(--vh-border)] flex flex-col"
                data-testid={`landing-pain-fix-card-${i}`}
              >
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--vh-accent)] mb-2">The pain</p>
                <h3 className="font-display text-xl text-[var(--vh-text-primary)] mb-2">{c.pain}</h3>
                <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed mb-6">{c.pain_body}</p>
                <div className="mt-auto pt-4 border-t border-[var(--vh-border)]">
                  <p className="text-xs uppercase tracking-[0.14em] text-[var(--vh-brand-primary)] mb-1">The fix</p>
                  <p className="font-display text-lg text-[var(--vh-text-primary)] mb-1">{c.fix}</p>
                  <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">{c.fix_body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/register"
              className="px-6 py-3 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] text-sm font-semibold shadow-sm"
              data-testid="landing-pain-fix-cta"
            >
              Start your vault — free for life →
            </Link>
            <Link
              to="/trust/data-minimisation"
              className="px-6 py-3 rounded-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)] text-sm font-medium"
              data-testid="landing-pain-fix-how"
            >
              How the vault works
            </Link>
          </div>
        </div>
      </section>

      {/* Persona switcher */}
      <section className="bg-[var(--vh-bg-muted)] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[var(--vh-accent)] mb-2">Built for everyone in the hire</p>
              <h2 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)]">Pick your seat at the table.</h2>
            </div>
            <div role="tablist" aria-label="Persona benefits" className="inline-flex flex-wrap p-1 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-full" data-testid="landing-persona-tabs">
              {LANDING_PERSONAS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={active === p.id}
                  onClick={() => setActive(p.id)}
                  data-testid={`landing-tab-${p.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${active === p.id ? 'bg-[var(--vh-brand-primary)] text-white' : 'text-[var(--vh-text-secondary)] hover:text-[var(--vh-brand-primary)]'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {persona && <LandingPersonaPanel persona={persona} />}
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="p-7 rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]">
            <div className="text-xs uppercase tracking-widest text-[var(--vh-accent)] mb-3">A new principle</div>
            <h3 className="font-display text-xl text-[var(--vh-text-primary)] mb-2">Retention is the liability.</h3>
            <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">Legacy ATSs were built when data was an asset. That world is gone. Vault Hire is built for the world we live in now — where every record you don’t hold is one you can’t lose, leak, or be fined for.</p>
          </div>
          <div className="p-7 rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]">
            <div className="text-xs uppercase tracking-widest text-[var(--vh-accent)] mb-3">Banking ready</div>
            <h3 className="font-display text-xl text-[var(--vh-text-primary)] mb-2">SMCR, SYSC 22, FINRA U4.</h3>
            <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">Six-year lookback regulatory references. FCA-certified role pre-checks. Regulated identifiers are verified, never stored as raw scans.</p>
          </div>
          <div className="p-7 rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]">
            <div className="text-xs uppercase tracking-widest text-[var(--vh-accent)] mb-3">No vendor lock-in</div>
            <h3 className="font-display text-xl text-[var(--vh-text-primary)] mb-2">Export everything. Anywhere.</h3>
            <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">One-click GDPR Article 15 download in clean JSON, ready to drop into the next employer or HRIS.</p>
          </div>
        </div>

        {/* Teaser into the NDA-gated sales briefing */}
        <div className="mt-10 p-7 md:p-9 rounded-3xl bg-[var(--vh-brand-primary)] text-white flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-testid="landing-nda-teaser">
          <div className="md:max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-white/70 mb-2">For banking buyers · NDA-gated</p>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">The case against legacy ATS architecture.</h3>
            <p className="text-sm text-white/80 leading-relaxed">A board-ready briefing for the Senior Manager who will personally sign off on this system under SMCR. Schema diff, real audit row, the line that closes the deal. NDA + owner approval required.</p>
          </div>
          <a
            href="/walkthrough/"
            data-testid="landing-nda-teaser-cta"
            className="shrink-0 inline-flex items-center justify-center px-6 py-3 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] text-sm font-semibold"
          >
            Request the briefing →
          </a>
        </div>
      </section>

      <footer className="border-t border-[var(--vh-border)] bg-[var(--vh-bg-muted)] py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap gap-6 items-center justify-between text-xs text-[var(--vh-text-muted)]">
          <div>© {new Date().getFullYear()} Vault Hire · vault-hire.com</div>
          <div className="flex flex-wrap gap-5">
            <Link to="/trust" className="hover:text-[var(--vh-brand-primary)]">Trust centre</Link>
            <Link to="/trust/data-minimisation" className="hover:text-[var(--vh-brand-primary)]">Privacy</Link>
            <a href="/legal/" className="hover:text-[var(--vh-brand-primary)]" data-testid="landing-footer-legal">Legal library</a>
            <Link to="/pricing" className="hover:text-[var(--vh-brand-primary)]">Pricing</Link>
            <Link to="/companies" className="hover:text-[var(--vh-brand-primary)]">Companies</Link>
            <Link to="/jobs" className="hover:text-[var(--vh-brand-primary)]">Jobs board</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password, mfaCode || null);
      // Honor redirect param if present (e.g. coming back from a 401)
      const redirect = new URLSearchParams(window.location.search).get('redirect');
      if (redirect && redirect.startsWith('/')) {
        navigate(redirect);
        return;
      }
      if (user.role === 'candidate') {
        navigate('/candidate/dashboard');
      } else if (user.role === 'employer') {
        navigate('/employer/dashboard');
      } else if (user.role === 'hiring_manager') {
        navigate('/hiring-manager');
      } else if (user.role === 'admin' || user.role === 'owner') {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'MFA code required') {
        setNeedsMfa(true);
        setError('Please enter your MFA code');
      } else {
        setError(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center px-4 py-10">
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl border border-[var(--vh-border)] p-8 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)] mb-6 justify-center" data-testid="login-logo">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <h2 className="font-display text-3xl text-center mb-2 text-[var(--vh-text-primary)]" data-testid="login-page-title">Welcome back</h2>
        <p className="text-sm text-[var(--vh-text-secondary)] text-center mb-6">Sign in to your vault, Vault Hub or compliance portal.</p>
        
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              required
              data-testid="login-password-input"
            />
          </div>

          {needsMfa && (
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">MFA Code</label>
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                placeholder="6-digit code"
                data-testid="login-mfa-input"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white py-2.5 rounded-full font-medium transition disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="my-5 flex items-center gap-2 text-xs text-[var(--vh-text-muted)]">
          <span className="flex-1 h-px bg-[var(--vh-border)]" />
          OR
          <span className="flex-1 h-px bg-[var(--vh-border)]" />
        </div>
        <PasskeyLoginPanel email={email} />
        <div className="mt-2">
          <MagicLinkPanel />
        </div>

        <div className="mt-4 text-center text-sm">
          <Link to="/forgot-password" className="text-[var(--vh-brand-primary)] hover:underline" data-testid="login-forgot-password-link">
            Forgot password?
          </Link>
        </div>
        <div className="mt-3 text-center text-sm">
          <span className="text-[var(--vh-text-muted)]">Don’t have an account? </span>
          <Link to="/register" className="text-[var(--vh-accent)] hover:underline font-medium">Create one</Link>
        </div>
        <div className="mt-2 text-center text-xs">
          <Link to="/" className="text-[var(--vh-text-muted)] hover:text-[var(--vh-brand-primary)]">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('candidate');
  const [companyName, setCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptCrossBorder, setAcceptCrossBorder] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [duplicateCheckBusy, setDuplicateCheckBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Soft duplicate check — fires on blur of fullName/email/DOB once all three are set.
  const runDuplicateCheck = async () => {
    if (role !== 'candidate') return;
    if (!fullName.trim() || !email.trim()) return;
    setDuplicateCheckBusy(true);
    try {
      const { data } = await axios.post(`${API}/duplicate-check`, {
        full_name: fullName,
        email,
        date_of_birth: dateOfBirth || null,
      });
      setDuplicateWarning(data && data.matches && data.matches.length > 0 ? data : null);
    } catch (e) {
      // Soft check — never block UI on failure
      setDuplicateWarning(null);
    } finally {
      setDuplicateCheckBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptPrivacy || !acceptCrossBorder) {
      setError('Please accept the Privacy Notice and the cross-border data transfer terms to continue.');
      return;
    }
    if (duplicateWarning && duplicateWarning.strength === 'blocking') {
      setError(duplicateWarning.matches[0]?.advice || 'A vault already exists for this person. Please sign in instead.');
      return;
    }
    setLoading(true);

    try {
      const userData = {
        email,
        password,
        full_name: fullName,
        role,
        company_name: (role === 'employer' || role === 'hiring_manager') ? companyName : null,
        company_number: role === 'employer' ? companyNumber : null,
        consents: ['terms_of_service', 'privacy_policy', 'cross_border_transfer']
      };
      const user = await register(userData);
      if (user.role === 'candidate') {
        navigate('/candidate/dashboard');
      } else if (user.role === 'employer') {
        navigate('/employer/dashboard');
      } else if (user.role === 'hiring_manager') {
        navigate('/hiring-manager');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center px-4 py-10">
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl border border-[var(--vh-border)] p-8 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)] mb-6 justify-center" data-testid="register-logo">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <h2 className="font-display text-3xl text-center mb-2 text-[var(--vh-text-primary)]" data-testid="register-page-title">Create your account</h2>
        <p className="text-sm text-[var(--vh-text-secondary)] text-center mb-6">A vault you own, a Vault Hub that respects it.</p>
        
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded mb-4" data-testid="register-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Full name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              onBlur={runDuplicateCheck}
              className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              required
              data-testid="register-fullname-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={runDuplicateCheck}
              className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              required
              data-testid="register-email-input"
            />
          </div>

          {role === 'candidate' && (
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Date of birth <span className="text-[var(--vh-text-muted)] font-normal">(optional, helps prevent duplicate vaults)</span></label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                onBlur={runDuplicateCheck}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                data-testid="register-dob-input"
              />
            </div>
          )}

          {duplicateWarning && duplicateWarning.matches?.length > 0 && (
            <div
              className={`p-3 rounded-lg border text-sm ${
                duplicateWarning.strength === 'blocking'
                  ? 'bg-amber-50 border-amber-300 text-amber-900'
                  : 'bg-blue-50 border-blue-300 text-blue-900'
              }`}
              data-testid="register-duplicate-warning"
            >
              <p className="font-semibold mb-1">
                {duplicateWarning.strength === 'blocking' ? 'Possible existing vault detected' : 'Heads up'}
              </p>
              <p>{duplicateWarning.matches[0].advice}</p>
              <p className="mt-2 text-xs">
                <Link to="/login" className="underline font-medium">Sign in to your existing vault →</Link>
                {' '}or{' '}
                <a href="mailto:support@vault-hire.com" className="underline">contact support</a>
                {' '}if you need help recovering access.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              required
              data-testid="register-password-input"
            />
            <p className="text-xs text-[var(--vh-text-muted)] mt-1">Minimum 12 characters · upper + lower + digit + symbol.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">I am a:</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              data-testid="register-role-select"
            >
              <option value="candidate">Candidate</option>
              <option value="employer">Employer / Recruiter</option>
              <option value="hiring_manager">Hiring Manager</option>
            </select>
          </div>

          {role === 'hiring_manager' && (
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Company name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                required
                data-testid="register-hm-company-name"
                placeholder="e.g. HSBC Holdings plc"
              />
              <p className="text-xs text-[var(--vh-text-muted)] mt-1">A recruiter must add you to a requisition’s hiring_manager_email field before candidates are visible.</p>
            </div>
          )}

          {role === 'employer' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Company name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                  required
                  data-testid="register-company-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Company number (optional)</label>
                <input
                  type="text"
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                  data-testid="register-company-number-input"
                />
              </div>
            </>
          )}

          <div className="space-y-3 border-t border-[var(--vh-border)] pt-4 mt-2">
            <label className="flex items-start gap-2 text-xs text-[var(--vh-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={acceptPrivacy}
                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-[var(--vh-brand-primary)] rounded shrink-0"
                data-testid="register-privacy-consent"
                required
              />
              <span>
                I have read and accept the Vault Hire <Link to="/trust/data-minimisation" target="_blank" className="text-[var(--vh-brand-primary)] underline">Privacy Notice</Link> and consent to my personal data being processed for the purpose of operating my account, in accordance with UK GDPR Article 6(1)(b).
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[var(--vh-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={acceptCrossBorder}
                onChange={(e) => setAcceptCrossBorder(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-[var(--vh-brand-primary)] rounded shrink-0"
                data-testid="register-cross-border-consent"
                required
              />
              <span>
                I explicitly consent to my personal data being transferred outside my home jurisdiction (UK/EU/US) to Vault Hire’s sub-processors, protected by <Link to="/trust" target="_blank" className="text-[var(--vh-brand-primary)] underline">Standard Contractual Clauses</Link> (UK GDPR Art. 49(1)(a)).
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !acceptPrivacy || !acceptCrossBorder}
            className="w-full bg-[var(--vh-accent)] hover:bg-[var(--vh-accent-hover)] text-white py-2.5 rounded-full font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-submit-button"
          >
            {loading ? 'Creating account…' : 'Create account →'}
          </button>
        </form>

        <div className="mt-4 text-center text-sm">
          <span className="text-[var(--vh-text-muted)]">Already have an account? </span>
          <Link to="/login" className="text-[var(--vh-brand-primary)] hover:underline font-medium">Sign in</Link>
        </div>
        <div className="mt-2 text-center text-xs">
          <Link to="/" className="text-[var(--vh-text-muted)] hover:text-[var(--vh-brand-primary)]">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError('Too many attempts. Please wait 15 minutes and try again.');
      } else {
        // Backend deliberately returns 200 for unknown emails (anti-enumeration),
        // so any thrown error here is a server problem, not a user-facing one.
        setError('We could not process your request. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center px-4 py-10">
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl border border-[var(--vh-border)] p-8 w-full max-w-md" data-testid="forgot-password-page">
        <h2 className="font-display text-3xl text-center mb-2 text-[var(--vh-text-primary)]" data-testid="forgot-password-title">Forgot password</h2>
        <p className="text-sm text-[var(--vh-text-secondary)] text-center mb-6">
          Enter your account email. We’ll send a one-hour, single-use reset link if the account exists.
        </p>

        {sent ? (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 px-4 py-3 rounded mb-4" data-testid="forgot-password-sent">
            <p className="font-medium">Check your email.</p>
            <p className="text-sm mt-1">If <strong>{email}</strong> matches an account, a reset link has been sent. The link expires in 1 hour.</p>
            <p className="text-xs mt-2 text-emerald-800">Tip: also check your spam folder. Email delivery is currently logged to the server console in this preview environment — contact support if you don’t receive it within 5 minutes.</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded mb-4" data-testid="forgot-password-error">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white py-2.5 rounded-full font-medium transition disabled:opacity-50"
                data-testid="forgot-password-submit-button"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-[var(--vh-brand-primary)] hover:underline" data-testid="forgot-password-back-to-login">
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center px-4">
        <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl border border-[var(--vh-border)] p-8 w-full max-w-md text-center" data-testid="reset-password-no-token">
          <h2 className="font-display text-2xl mb-3 text-[var(--vh-text-primary)]">Invalid link</h2>
          <p className="text-sm text-[var(--vh-text-secondary)] mb-4">This password reset link is missing or malformed. Request a new one.</p>
          <Link to="/forgot-password" className="text-[var(--vh-brand-primary)] hover:underline">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center px-4 py-10">
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl border border-[var(--vh-border)] p-8 w-full max-w-md" data-testid="reset-password-page">
        <h2 className="font-display text-3xl text-center mb-2 text-[var(--vh-text-primary)]" data-testid="reset-password-title">Set a new password</h2>
        <p className="text-sm text-[var(--vh-text-secondary)] text-center mb-6">
          Minimum 12 characters, including upper-case, lower-case, digit, and symbol.
        </p>

        {done ? (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 px-4 py-3 rounded text-center" data-testid="reset-password-done">
            <p className="font-medium">Password updated.</p>
            <p className="text-sm mt-1">Redirecting to sign in…</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded mb-4" data-testid="reset-password-error">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">New password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                  required
                  minLength={12}
                  data-testid="reset-password-new-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--vh-text-secondary)]">Confirm new password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                  required
                  minLength={12}
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white py-2.5 rounded-full font-medium transition disabled:opacity-50"
                data-testid="reset-password-submit-button"
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-[var(--vh-brand-primary)] hover:underline">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Magic Link — passwordless sign-in (respects MFA)
// ---------------------------------------------------------------------------
function MagicLinkPanel() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/magic-link/request`, { email });
      setSent(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError('Too many requests. Try again in 15 minutes.');
      } else {
        setError('Could not send. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] bg-[var(--vh-bg-surface)] py-2 rounded-full hover:bg-[var(--vh-bg-muted)] transition text-sm font-medium"
        data-testid="login-magic-link-button"
      >
        Sign in with a magic link
      </button>
    );
  }

  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 px-3 py-2 rounded-lg text-xs" data-testid="magic-link-sent">
        Check <strong>{email}</strong> for a single-use sign-in link (valid 15 min). MFA, if enabled, will still be required after the click.
      </div>
    );
  }

  return (
    <form onSubmit={handleSend} className="space-y-2" data-testid="magic-link-panel">
      <p className="text-xs text-[var(--vh-text-secondary)]">Email a single-use sign-in link. No password required.</p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        placeholder="you@example.com"
        className="w-full px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
        data-testid="magic-link-email-input"
      />
      {error && <div className="text-xs text-red-700" data-testid="magic-link-error">{error}</div>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[var(--vh-brand-primary)] text-white py-1.5 rounded-full text-sm hover:bg-[var(--vh-brand-primary-hover)] disabled:opacity-50"
          data-testid="magic-link-submit"
        >
          {loading ? 'Sending…' : 'Send link'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setEmail(''); setError(''); }}
          className="px-3 py-1.5 border border-[var(--vh-border)] rounded-full text-sm text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-muted)]"
          data-testid="magic-link-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function MagicLinkConsumePage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('consuming'); // consuming | mfa | error
  const [error, setError] = useState('');
  const [mfaSession, setMfaSession] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const consumedRef = useState({ done: false })[0];

  const routeByRole = useCallback((user) => {
    if (!user) return navigate('/login');
    if (user.role === 'candidate') navigate('/candidate/dashboard');
    else if (user.role === 'employer') navigate('/employer/dashboard');
    else if (user.role === 'hiring_manager') navigate('/hiring-manager');
    else if (user.role === 'admin' || user.role === 'owner') navigate('/admin/dashboard');
    else navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (!token || consumedRef.done) return;
    consumedRef.done = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/magic-link/consume`, { params: { token } });
        if (data.mfa_required) {
          setMfaSession(data.mfa_session_token);
          setStatus('mfa');
        } else {
          await checkAuth();
          routeByRole(data.user);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'This magic link is invalid or expired. Please request a new one.');
        setStatus('error');
      }
    })();
  }, [token, checkAuth, routeByRole, consumedRef]);

  const submitMfa = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/magic-link/verify-mfa`, {
        mfa_session_token: mfaSession, mfa_code: mfaCode,
      });
      await checkAuth();
      routeByRole(data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center px-4 py-10">
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl border border-[var(--vh-border)] p-8 w-full max-w-md" data-testid="magic-link-page">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)] mb-6 justify-center">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        {!token ? (
          <div data-testid="magic-link-no-token" className="text-center">
            <h2 className="font-display text-2xl text-[var(--vh-text-primary)] mb-2">Invalid link</h2>
            <p className="text-sm text-[var(--vh-text-secondary)] mb-4">This sign-in link is missing or malformed.</p>
            <Link to="/login" className="text-[var(--vh-brand-primary)] hover:underline">Back to sign in</Link>
          </div>
        ) : status === 'consuming' ? (
          <div data-testid="magic-link-consuming" className="text-center">
            <h2 className="font-display text-2xl text-[var(--vh-text-primary)] mb-2">Signing you in…</h2>
            <p className="text-sm text-[var(--vh-text-secondary)]">Validating your magic link.</p>
          </div>
        ) : status === 'error' ? (
          <div data-testid="magic-link-error-state" className="text-center">
            <h2 className="font-display text-2xl text-[var(--vh-text-primary)] mb-2">Link expired</h2>
            <p className="text-sm text-[var(--vh-text-secondary)] mb-4">{error}</p>
            <Link to="/login" className="text-[var(--vh-brand-primary)] hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <div data-testid="magic-link-mfa-step">
            <h2 className="font-display text-2xl text-center mb-2 text-[var(--vh-text-primary)]">One more step</h2>
            <p className="text-sm text-[var(--vh-text-secondary)] text-center mb-6">
              Multi-factor authentication is enabled on this account. Enter the 6-digit code from your authenticator app.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-2 rounded mb-4 text-sm" data-testid="magic-link-mfa-error">
                {error}
              </div>
            )}
            <form onSubmit={submitMfa} className="space-y-4">
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                inputMode="numeric"
                autoFocus
                required
                className="w-full px-4 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)] text-center tracking-widest text-lg focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
                data-testid="magic-link-mfa-input"
              />
              <button
                type="submit"
                disabled={submitting || mfaCode.length !== 6}
                className="w-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white py-2.5 rounded-full font-medium disabled:opacity-50"
                data-testid="magic-link-mfa-submit"
              >
                {submitting ? 'Verifying…' : 'Verify and sign in'}
              </button>
            </form>
            <div className="mt-4 text-center text-sm">
              <Link to="/login" className="text-[var(--vh-text-muted)] hover:text-[var(--vh-brand-primary)]">Cancel and use password instead</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Passkey login panel (login screen) + passkey management page
// ---------------------------------------------------------------------------
function PasskeyLoginPanel({ email }) {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const supported = isWebAuthnSupported();

  const routeByRole = useCallback((user) => {
    if (!user) return navigate('/login');
    if (user.role === 'candidate') navigate('/candidate/dashboard');
    else if (user.role === 'employer') navigate('/employer/dashboard');
    else if (user.role === 'hiring_manager') navigate('/hiring-manager');
    else if (user.role === 'admin' || user.role === 'owner') navigate('/admin/dashboard');
    else navigate('/');
  }, [navigate]);

  if (!supported) return null;

  const handle = async () => {
    setError('');
    if (!email) {
      setError('Enter your email above first, then click "Sign in with passkey".');
      return;
    }
    setLoading(true);
    try {
      const user = await signInWithPasskey({ email });
      await checkAuth();
      routeByRole(user);
    } catch (err) {
      if (err.name === 'NotAllowedError') setError('Sign-in was cancelled.');
      else if (err.name === 'SecurityError') setError('This site is not configured for passkeys.');
      else setError(err.response?.data?.detail || err.message || 'Passkey sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="passkey-login-panel">
      <button
        type="button"
        onClick={handle}
        disabled={loading}
        className="w-full border border-[var(--vh-border)] text-[var(--vh-brand-primary)] bg-[var(--vh-bg-muted)] hover:bg-[var(--vh-border)] py-2 rounded-full transition text-sm font-medium disabled:opacity-50"
        data-testid="login-passkey-button"
      >
        {loading ? 'Waiting for passkey…' : 'Sign in with passkey'}
      </button>
      {error && <p className="text-xs text-red-700" data-testid="passkey-login-error">{error}</p>}
    </div>
  );
}

function PasskeyManagerPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/passkeys`);
      setPasskeys(data || []);
    } catch (e) { logger.error('passkeys list', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!authLoading && user) load();
  }, [user, authLoading, load]);

  const addPasskey = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      await registerPasskey({ label: label || 'Passkey' });
      setLabel('');
      setInfo('Passkey added — you can now sign in with it next time.');
      load();
    } catch (err) {
      if (err.name === 'NotAllowedError') setError('Cancelled or timed out.');
      else if (err.name === 'InvalidStateError') setError('This authenticator is already enrolled for this account.');
      else setError(err.response?.data?.detail || err.message || 'Could not register passkey.');
    } finally { setBusy(false); }
  };

  const deletePasskey = async (id) => {
    if (!window.confirm('Remove this passkey? You will no longer be able to sign in with it.')) return;
    try {
      await axios.delete(`${API}/me/passkeys/${id}`);
      load();
    } catch (e) { setError('Failed to remove.'); }
  };

  if (authLoading) return <div className="p-12 text-center text-gray-500">Loading…</div>;
  if (!user) { navigate('/login'); return null; }

  const supported = isWebAuthnSupported();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="passkey-manager-page">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-sm border border-slate-200 p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">Security</p>
            <h1 className="text-2xl font-semibold text-slate-900">Passkeys</h1>
            <p className="text-sm text-slate-500 mt-1">
              Phishing-resistant, biometric-protected sign-in. Replaces passwords on devices that support them.
            </p>
          </div>
          <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900" data-testid="passkey-back-button">← Back</button>
        </div>

        {!supported ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm" data-testid="passkey-unsupported">
            Your browser does not support passkeys. Use a recent version of Safari, Chrome, Firefox, or Edge.
          </div>
        ) : (
          <>
            <section className="border-t border-slate-100 pt-5 mb-6">
              <h2 className="text-sm font-semibold text-slate-700 mb-2">Add a new passkey</h2>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. iPhone Face ID, YubiKey 5C"
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="passkey-label-input"
                />
                <button
                  onClick={addPasskey}
                  disabled={busy}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg disabled:opacity-50"
                  data-testid="passkey-add-button"
                >
                  {busy ? 'Waiting…' : 'Register passkey'}
                </button>
              </div>
              {error && <p className="text-xs text-red-700 mt-2" data-testid="passkey-add-error">{error}</p>}
              {info && <p className="text-xs text-emerald-700 mt-2" data-testid="passkey-add-info">{info}</p>}
              <p className="text-xs text-slate-400 mt-2">
                Your device will prompt you to authenticate with biometrics or a security key. The private key never leaves your device.
              </p>
            </section>

            <section className="border-t border-slate-100 pt-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Your passkeys</h2>
              {loading ? (
                <div className="text-sm text-slate-400">Loading…</div>
              ) : passkeys.length === 0 ? (
                <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500" data-testid="passkey-empty">
                  No passkeys yet. Add one to sign in faster (and ditch passwords).
                </div>
              ) : (
                <ul className="space-y-2" data-testid="passkey-list">
                  {passkeys.map((p) => (
                    <li key={p.id} data-testid={`passkey-row-${p.id.slice(0, 8)}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{p.label || 'Passkey'}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Added {p.created_at?.slice(0, 10) || '—'}
                          {p.last_used_at ? ` · last used ${p.last_used_at.slice(0, 10)}` : ' · never used'}
                          {p.transports?.length ? ` · ${p.transports.join(', ')}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => deletePasskey(p.id)}
                        className="text-xs text-red-600 hover:text-red-700 hover:underline shrink-0 ml-3"
                        data-testid={`passkey-delete-${p.id.slice(0, 8)}`}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}




function CandidateApplicationPacks() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/me/applications`)
      .then((r) => { if (mounted) setApps(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);
  if (loading) return null;
  if (apps.length === 0) return null;
  return (
    <div className="space-y-4" data-testid="candidate-application-packs">
      <h3 className="text-lg font-semibold mt-6">Required pack for each application</h3>
      {apps.map((a) => (
        <CandidatePackPanel key={a.id} applicationId={a.id} />
      ))}
    </div>
  );
}

function RecruiterHQAiToggle() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/recruiter-hq/ai-assist`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const toggle = async () => {
    setSaving(true);
    try {
      const { data: r } = await axios.put(`${API}/admin/recruiter-hq/ai-assist`, { enabled: !data.enabled });
      setData({ ...data, enabled: r.enabled });
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="ml-4 px-4 py-2 rounded-lg bg-white border border-slate-200 flex items-center gap-3 text-sm" data-testid="ai-assist-toggle">
      <span className="text-xs uppercase tracking-wider text-slate-500">NBA AI assist (roadmap)</span>
      <button onClick={toggle} disabled={saving}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${data.enabled ? 'bg-emerald-600' : 'bg-slate-300'}`}
        data-testid="ai-assist-toggle-btn">
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition ${data.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function CandidateDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('employment');
  const [employmentRecords, setEmploymentRecords] = useState([]);
  const [accessLinks, setAccessLinks] = useState([]);
  const [references, setReferences] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [parsedCV, setParsedCV] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [trustScore, setTrustScore] = useState(null);
  const [linkViewers, setLinkViewers] = useState({});
  const [showAddEmployment, setShowAddEmployment] = useState(false);
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [showRequestReference, setShowRequestReference] = useState(false);
  const [selectedEmployment, setSelectedEmployment] = useState(null);
  const navigate = useNavigate();

  const [newEmployment, setNewEmployment] = useState({
    company_name: '',
    job_title: '',
    start_date: '',
    end_date: '',
    current: false,
    responsibilities: '',
    achievements: ''
  });

  const [newLink, setNewLink] = useState({
    expiry_hours: 24,
    include_employment: true,
    include_documents: true,
    include_pii: false,
    include_ai_profile: false,
    include_compliance_readiness: false
  });

  // AI Profile state
  const [aiProfileData, setAiProfileData] = useState({ approved_version: null, versions: [] });
  const [aiProfileViews, setAiProfileViews] = useState([]);
  const [generatingProfile, setGeneratingProfile] = useState(false);
  const [aiNotes, setAiNotes] = useState('');
  const [aiTargetRole, setAiTargetRole] = useState('');
  const [expandedProfileId, setExpandedProfileId] = useState(null);

  const [referenceRequest, setReferenceRequest] = useState({
    employment_record_id: '',
    employer_email: '',
    message: '',
    anonymous: false
  });

  const loadEmploymentRecords = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/employment`);
      setEmploymentRecords(data);
    } catch (err) {
      logger.error('Failed to load employment records', err);
    }
  }, []);

  const loadAccessLinks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/access-links`);
      setAccessLinks(data);
    } catch (err) {
      logger.error('Failed to load access links', err);
    }
  }, []);

  const loadReferences = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/references/sent`);
      setReferences(data);
    } catch (err) {
      logger.error('Failed to load references', err);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/documents`);
      setDocuments(data);
    } catch (err) {
      logger.error('Failed to load documents', err);
    }
  }, []);

  const loadTrustScore = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/trust-score`);
      setTrustScore(data);
    } catch (err) {
      logger.error('Failed to load trust score', err);
    }
  }, []);

  const loadLinkViewers = useCallback(async (tokenId) => {
    try {
      const { data } = await axios.get(`${API}/vault/access-links/${tokenId}/viewers`);
      setLinkViewers(prev => ({...prev, [tokenId]: data}));
    } catch (err) {
      logger.error('Failed to load viewers', err);
    }
  }, []);

  const loadAIProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/ai-profile`);
      setAiProfileData(data);
    } catch (err) {
      logger.error('Failed to load AI profile', err);
    }
  }, []);

  const loadAIProfileViews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/ai-profile/views`);
      setAiProfileViews(data);
    } catch (err) {
      logger.error('Failed to load AI profile views', err);
    }
  }, []);

  const handleGenerateAIProfile = async () => {
    setGeneratingProfile(true);
    try {
      await axios.post(`${API}/vault/ai-profile/generate`, {
        notes: aiNotes || null,
        target_role: aiTargetRole || null
      });
      setAiNotes('');
      setAiTargetRole('');
      await loadAIProfile();
    } catch (err) {
      alert('Failed to generate AI profile: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingProfile(false);
    }
  };

  const handleApproveProfile = async (profileId) => {
    try {
      await axios.post(`${API}/vault/ai-profile/${profileId}/approve`);
      await loadAIProfile();
    } catch (err) {
      alert('Failed to approve: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUnapproveProfile = async (profileId) => {
    if (!window.confirm('Pull this profile back to draft? Employers will no longer see it via existing access links.')) return;
    try {
      await axios.post(`${API}/vault/ai-profile/${profileId}/unapprove`);
      await loadAIProfile();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Delete this draft profile version?')) return;
    try {
      await axios.delete(`${API}/vault/ai-profile/${profileId}`);
      await loadAIProfile();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRevokeViewer = async (tokenId, userId) => {
    if (!window.confirm("Revoke this viewer's access?")) return;
    try {
      await axios.delete(`${API}/vault/access-links/${tokenId}/viewers/${userId}`);
      loadLinkViewers(tokenId);
      alert('Viewer access revoked');
    } catch (err) {
      alert('Failed to revoke access');
    }
  };

  useEffect(() => {
    loadEmploymentRecords();
    loadAccessLinks();
    loadReferences();
    loadDocuments();
    loadTrustScore();
    loadAIProfile();
    loadAIProfileViews();
  }, [loadEmploymentRecords, loadAccessLinks, loadReferences, loadDocuments, loadTrustScore, loadAIProfile, loadAIProfileViews]);

  const handleFileUpload = async (file, documentType) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/vault/documents/upload?document_type=${documentType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      loadDocuments();
      alert('Document uploaded successfully!');
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCVParse = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/vault/cv/parse`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setParsedCV(data.parsed_data);
      // Also save the CV as a document
      await handleFileUpload(file, 'cv');
    } catch (err) {
      alert('CV parsing failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/vault/documents/${docId}`);
      loadDocuments();
    } catch (err) {
      alert('Delete failed');
    }
  };

  const handleAddEmployment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vault/employment`, newEmployment);
      setShowAddEmployment(false);
      setNewEmployment({
        company_name: '',
        job_title: '',
        start_date: '',
        end_date: '',
        current: false,
        responsibilities: '',
        achievements: ''
      });
      loadEmploymentRecords();
      loadAIProfile();
      loadTrustScore();
    } catch (err) {
      alert('Failed to add employment record');
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/vault/access-links`, newLink);
      setShowCreateLink(false);
      setNewLink({
        expiry_hours: 24,
        include_employment: true,
        include_documents: true,
        include_pii: false,
        include_ai_profile: false,
        include_compliance_readiness: false
      });
      loadAccessLinks();
      alert(`Link created! Share this URL:\n${data.share_url}`);
    } catch (err) {
      alert('Failed to create access link');
    }
  };

  const handleRequestReference = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vault/references/request`, referenceRequest);
      setShowRequestReference(false);
      setReferenceRequest({
        employment_record_id: '',
        employer_email: '',
        message: '',
        anonymous: false
      });
      loadReferences();
      alert('Reference request sent!');
    } catch (err) {
      alert('Failed to send reference request');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm" data-testid="candidate-dashboard">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="text-xl font-bold text-blue-900">Vault Hire</div>
          <div className="flex gap-4 items-center">
            <Link to="/companies" data-testid="candidate-companies-link" className="text-sm text-blue-700 hover:underline">
              Browse companies
            </Link>
            <MessageBell onOpen={() => navigate('/messages')} />
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              data-testid="candidate-logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <CandidateOffersBanner />
        <CandidateMutualInterestBanner />
        <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
          <h1 className="text-3xl font-bold">Welcome, {user?.full_name}</h1>
          {trustScore && (
            <div className="bg-white rounded-lg shadow-lg p-4 border-l-4 border-blue-500" data-testid="trust-score-widget">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{trustScore.total_score}</div>
                  <div className="text-xs text-gray-500">/ 100</div>
                </div>
                <div>
                  <div className="font-semibold">Trust Score</div>
                  <div className="text-sm text-blue-600 font-medium">{trustScore.trust_level}</div>
                  <button
                    onClick={() => alert(
                      `Trust Score Breakdown:\n\n` +
                      `Employment History: ${trustScore.breakdown.employment_history.score}/${trustScore.breakdown.employment_history.max} (${trustScore.breakdown.employment_history.count} records)\n` +
                      `Verified References: ${trustScore.breakdown.verified_references.score}/${trustScore.breakdown.verified_references.max} (${trustScore.breakdown.verified_references.count} refs)\n` +
                      `Verified Employer Refs: ${trustScore.breakdown.verified_employer_refs.score}/${trustScore.breakdown.verified_employer_refs.max}\n` +
                      `Documents: ${trustScore.breakdown.documents.score}/${trustScore.breakdown.documents.max} (${trustScore.breakdown.documents.count} files)\n` +
                      `Vault Age: ${trustScore.breakdown.vault_age_months.score}/${trustScore.breakdown.vault_age_months.max} (${trustScore.breakdown.vault_age_months.count} months)`
                    )}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="trust-score-breakdown-button"
                  >
                    View breakdown →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('employment')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'employment' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="employment-tab"
          >
            Employment History
          </button>
          <button
            onClick={() => setActiveTab('links')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'links' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="access-links-tab"
          >
            Access Links
          </button>
          <button
            onClick={() => setActiveTab('references')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'references' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="references-tab"
          >
            References
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'documents' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="documents-tab"
          >
            Documents
          </button>
          <button
            onClick={() => setActiveTab('ai-profile')}
            className={`px-6 py-2 rounded-lg transition relative ${activeTab === 'ai-profile' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="ai-profile-tab"
          >
            AI Profile
            {aiProfileData.approved_version && !aiProfileData.is_stale && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-500 text-white">v{aiProfileData.approved_version.version}</span>
            )}
            {aiProfileData.is_stale && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-500 text-white" data-testid="ai-profile-stale-tab-badge">stale</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('compliance-pack')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'compliance-pack' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="compliance-pack-tab"
          >
            🛡️ Compliance Pack
          </button>
        </div>

        {activeTab === 'employment' && (
          <div>
            {aiProfileData.is_stale && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4 rounded flex items-center justify-between" data-testid="employment-stale-nudge">
                <p className="text-sm text-amber-900">
                  ⚠️ Your <strong>AI Profile</strong> (v{aiProfileData.approved_version?.version}) is out of date — employers viewing your vault are seeing the old version.
                </p>
                <button
                  onClick={() => setActiveTab('ai-profile')}
                  className="ml-3 px-3 py-1 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 whitespace-nowrap"
                >
                  Update Now
                </button>
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">Employment History</h2>
              <button
                onClick={() => setShowAddEmployment(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="add-employment-button"
              >
                + Add Employment
              </button>
            </div>

            {showAddEmployment && (
              <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h3 className="text-xl font-semibold mb-4">Add Employment Record</h3>
                <form onSubmit={handleAddEmployment} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Company Name</label>
                      <input
                        type="text"
                        value={newEmployment.company_name}
                        onChange={(e) => setNewEmployment({...newEmployment, company_name: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        required
                        data-testid="employment-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Job Title</label>
                      <input
                        type="text"
                        value={newEmployment.job_title}
                        onChange={(e) => setNewEmployment({...newEmployment, job_title: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        required
                        data-testid="employment-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Start Date</label>
                      <input
                        type="date"
                        value={newEmployment.start_date}
                        onChange={(e) => setNewEmployment({...newEmployment, start_date: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">End Date</label>
                      <input
                        type="date"
                        value={newEmployment.end_date}
                        onChange={(e) => setNewEmployment({...newEmployment, end_date: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        disabled={newEmployment.current}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newEmployment.current}
                        onChange={(e) => setNewEmployment({...newEmployment, current: e.target.checked, end_date: ''})}
                      />
                      <span className="text-sm font-medium">I currently work here</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Responsibilities</label>
                    <textarea
                      value={newEmployment.responsibilities}
                      onChange={(e) => setNewEmployment({...newEmployment, responsibilities: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Achievements</label>
                    <textarea
                      value={newEmployment.achievements}
                      onChange={(e) => setNewEmployment({...newEmployment, achievements: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows="3"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      data-testid="employment-save-button"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddEmployment(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-4">
              {employmentRecords.map((record) => (
                <div key={record.id} className="bg-white p-6 rounded-lg shadow" data-testid="employment-record">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-semibold">{record.job_title}</h3>
                      <p className="text-gray-600">{record.company_name}</p>
                      <p className="text-sm text-gray-500">
                        {record.start_date} - {record.current ? 'Present' : record.end_date}
                      </p>
                      {record.responsibilities && (
                        <p className="mt-2 text-sm">{record.responsibilities}</p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedEmployment(record);
                        setReferenceRequest({...referenceRequest, employment_record_id: record.id});
                        setShowRequestReference(true);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      data-testid="request-reference-button"
                    >
                      Request Reference
                    </button>
                  </div>
                </div>
              ))}
              {employmentRecords.length === 0 && (
                <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
                  No employment records yet. Add your first one!
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'links' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">Access Links</h2>
              <button
                onClick={() => setShowCreateLink(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="create-link-button"
              >
                + Create Link
              </button>
            </div>

            {showCreateLink && (
              <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h3 className="text-xl font-semibold mb-4">Create Access Link</h3>
                <form onSubmit={handleCreateLink} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Expiry (hours)</label>
                    <input
                      type="number"
                      value={newLink.expiry_hours}
                      onChange={(e) => setNewLink({...newLink, expiry_hours: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border rounded-lg"
                      required
                      min="1"
                      data-testid="link-expiry-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newLink.include_employment}
                        onChange={(e) => setNewLink({...newLink, include_employment: e.target.checked})}
                      />
                      <span className="text-sm font-medium">Include Employment History</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newLink.include_documents}
                        onChange={(e) => setNewLink({...newLink, include_documents: e.target.checked})}
                      />
                      <span className="text-sm font-medium">Include Documents</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newLink.include_pii}
                        onChange={(e) => setNewLink({...newLink, include_pii: e.target.checked})}
                      />
                      <span className="text-sm font-medium">Include Personal Information (Email, Phone)</span>
                    </label>
                    <label className="flex items-center gap-2" data-testid="link-include-ai-profile">
                      <input
                        type="checkbox"
                        checked={newLink.include_ai_profile}
                        onChange={(e) => setNewLink({...newLink, include_ai_profile: e.target.checked})}
                        disabled={!aiProfileData.approved_version}
                      />
                      <span className="text-sm font-medium">
                        Include AI Profile
                        {!aiProfileData.approved_version && (
                          <span className="ml-1 text-xs text-gray-500">(approve a profile first)</span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-testid="link-include-compliance-readiness">
                      <input
                        type="checkbox"
                        checked={!!newLink.include_compliance_readiness}
                        onChange={(e) => setNewLink({...newLink, include_compliance_readiness: e.target.checked})}
                      />
                      <span className="text-sm font-medium">
                        🛡️ Include Compliance Readiness signal
                        <span className="ml-1 text-xs text-gray-500">(score + tier only — never the documents)</span>
                      </span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      data-testid="link-create-button"
                    >
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateLink(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-4">
              {accessLinks.map((link) => (
                <div key={link.token_id} className="bg-white p-6 rounded-lg shadow" data-testid="access-link">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 mb-2">
                        Created: {new Date(link.created_date).toLocaleString()}
                      </p>
                      <p className="text-sm mb-2">
                        Expires: {new Date(link.expiry_date).toLocaleString()}
                        {link.expired && <span className="text-red-600 ml-2">(Expired)</span>}
                      </p>
                      <p className="text-sm mb-2">Accessed: {link.accessed_count} times</p>
                      <div className="bg-gray-100 p-3 rounded mt-2 break-all text-sm">
                        {link.share_url}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      {!link.expired && link.active && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(link.share_url);
                            alert('Link copied!');
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                        >
                          Copy Link
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (linkViewers[link.token_id]) {
                            setLinkViewers(prev => {
                              const newState = {...prev};
                              delete newState[link.token_id];
                              return newState;
                            });
                          } else {
                            loadLinkViewers(link.token_id);
                          }
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                        data-testid="view-viewers-button"
                      >
                        {linkViewers[link.token_id] ? 'Hide' : 'View'} Viewers
                      </button>
                    </div>
                  </div>
                  
                  {linkViewers[link.token_id] && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold mb-2">Authorized Viewers</h4>
                      {linkViewers[link.token_id].length === 0 ? (
                        <p className="text-sm text-gray-500">No one has accessed this link yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {linkViewers[link.token_id].map(viewer => (
                            <div key={viewer.user_id} className="flex justify-between items-center bg-gray-50 p-3 rounded" data-testid="viewer-item">
                              <div>
                                <p className="font-semibold text-sm">{viewer.full_name}</p>
                                <p className="text-xs text-gray-600">{viewer.email} • {viewer.role}</p>
                                <p className="text-xs text-gray-500">
                                  Accessed {viewer.access_count} times • Last: {new Date(viewer.last_accessed).toLocaleString()}
                                </p>
                              </div>
                              <button
                                onClick={() => handleRevokeViewer(link.token_id, viewer.user_id)}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                data-testid="revoke-viewer-button"
                              >
                                Revoke
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {accessLinks.length === 0 && (
                <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
                  No access links yet. Create one to share your vault!
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'references' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">Reference Requests</h2>

            <div className="grid lg:grid-cols-2 gap-4 mb-6" data-testid="candidate-vault-library-row">
              <ReferenceLibraryPanel />
              <RightToWorkPanel />
            </div>

            <CandidateApplicationPacks />

            {showRequestReference && selectedEmployment && (
              <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h3 className="text-xl font-semibold mb-4">Request Reference</h3>
                <p className="mb-4 text-sm text-gray-600">
                  For: {selectedEmployment.job_title} at {selectedEmployment.company_name}
                </p>
                <form onSubmit={handleRequestReference} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Employer Email</label>
                    <input
                      type="email"
                      value={referenceRequest.employer_email}
                      onChange={(e) => setReferenceRequest({...referenceRequest, employer_email: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      required
                      data-testid="reference-employer-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Message (Optional)</label>
                    <textarea
                      value={referenceRequest.message}
                      onChange={(e) => setReferenceRequest({...referenceRequest, message: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows="3"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={referenceRequest.anonymous}
                        onChange={(e) => setReferenceRequest({...referenceRequest, anonymous: e.target.checked})}
                        className="mt-1"
                        data-testid="anonymous-checkbox"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-semibold">Request anonymously</span>
                        <p className="text-xs text-gray-600 mt-1">
                          <strong className="text-blue-700">💡 Recommended:</strong> Keep your name visible for best response rates. 
                          Employers are more likely to respond when they recognize the candidate. 
                          Only enable anonymity if you have specific privacy concerns.
                        </p>
                      </div>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      data-testid="reference-send-button"
                    >
                      Send Request
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRequestReference(false);
                        setSelectedEmployment(null);
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-4">
              {references.map((ref) => (
                <div key={ref.id} className="bg-white p-6 rounded-lg shadow" data-testid="reference-request">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">{ref.company_name}</h3>
                      <p className="text-gray-600">{ref.job_title}</p>
                      <p className="text-sm text-gray-500 mt-2">To: {ref.employer_email}</p>
                      <p className="text-sm mt-1">
                        Status: <span className={`font-semibold ${ref.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                          {ref.status}
                        </span>
                      </p>
                      {ref.reference && (
                        <div className="mt-4 p-4 bg-green-50 rounded-lg" data-testid="received-reference">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-semibold">Reference Received</p>
                            {ref.reference.credibility_score !== undefined && (
                              <div className="text-right" data-testid="reference-credibility-badge">
                                <div className="text-xs text-gray-600">Verifier Credibility</div>
                                <div className="text-sm font-bold">
                                  {ref.reference.credibility_emoji} {ref.reference.credibility_level}
                                </div>
                                <div className="text-xs text-gray-500">{ref.reference.credibility_score}/100</div>
                              </div>
                            )}
                          </div>
                          {ref.reference.employer_name && (
                            <p className="text-xs text-gray-600 mb-2">From: {ref.reference.employer_name}</p>
                          )}
                          <p className="text-sm">Rating: {ref.reference.rating}/5</p>
                          <p className="text-sm mt-2">{ref.reference.comments}</p>
                          <p className="text-xs text-gray-500 mt-2">Relationship: {ref.reference.relationship}</p>
                          {ref.reference.would_rehire !== undefined && (
                            <p className="text-xs mt-1">
                              Would rehire: {ref.reference.would_rehire ? '✅ Yes' : '❌ No'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {references.length === 0 && (
                <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
                  No reference requests yet.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">Documents & CV Parser</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🔒</span>
                <div>
                  <p className="font-semibold text-blue-900">Privacy First</p>
                  <p className="text-sm text-blue-700">
                    All CV parsing happens locally on our server. Your PII data NEVER leaves the vault to be processed by external AI services.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-3">📄 Upload & Parse CV</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload your CV as PDF. We'll extract key information locally to help you fill out your profile.
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleCVParse(e.target.files[0])}
                  disabled={uploadingFile}
                  className="w-full p-2 border rounded"
                  data-testid="cv-upload-input"
                />
                {uploadingFile && <p className="text-sm text-blue-600 mt-2">Processing locally...</p>}
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-3">📎 Upload Other Documents</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload certificates, references, ID documents, or other files (Max 5MB).
                </p>
                <div className="space-y-2">
                  <select
                    id="docType"
                    className="w-full p-2 border rounded"
                    defaultValue="certificate"
                  >
                    <option value="certificate">Certificate</option>
                    <option value="reference">Reference Letter</option>
                    <option value="id">ID Document</option>
                    <option value="other">Other</option>
                  </select>
                  <input
                    type="file"
                    onChange={(e) => {
                      const docType = document.getElementById('docType').value;
                      handleFileUpload(e.target.files[0], docType);
                    }}
                    disabled={uploadingFile}
                    className="w-full p-2 border rounded"
                    data-testid="document-upload-input"
                  />
                </div>
              </div>
            </div>

            {parsedCV && (
              <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h3 className="text-lg font-semibold mb-3">🔍 CV Parsed Locally</h3>
                <div className="space-y-3 text-sm">
                  {parsedCV.emails.length > 0 && (
                    <div>
                      <strong>Emails found:</strong>
                      <ul className="ml-4">{parsedCV.emails.map((e) => <li key={`email-${e}`}>{e}</li>)}</ul>
                    </div>
                  )}
                  {parsedCV.phones.length > 0 && (
                    <div>
                      <strong>Phone numbers found:</strong>
                      <ul className="ml-4">{parsedCV.phones.map((p) => <li key={`phone-${p}`}>{p}</li>)}</ul>
                    </div>
                  )}
                  {parsedCV.potential_companies.length > 0 && (
                    <div>
                      <strong>Potential companies:</strong>
                      <ul className="ml-4">{parsedCV.potential_companies.slice(0, 5).map((c) => <li key={`company-${c}`}>{c}</li>)}</ul>
                    </div>
                  )}
                  {parsedCV.potential_titles.length > 0 && (
                    <div>
                      <strong>Potential job titles:</strong>
                      <ul className="ml-4">{parsedCV.potential_titles.slice(0, 5).map((t) => <li key={`title-${t}`}>{t}</li>)}</ul>
                    </div>
                  )}
                  {parsedCV.education.length > 0 && (
                    <div>
                      <strong>Education:</strong>
                      <ul className="ml-4">{parsedCV.education.map((e) => <li key={`edu-${e}`}>{e}</li>)}</ul>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setActiveTab('employment');
                      setShowAddEmployment(true);
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Use this data to add employment records →
                  </button>
                </div>
              </div>
            )}

            <h3 className="text-xl font-semibold mb-4">Your Documents</h3>
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white p-4 rounded-lg shadow flex justify-between items-center" data-testid="document-item">
                  <div>
                    <p className="font-semibold">{doc.filename}</p>
                    <p className="text-xs text-gray-500">
                      {doc.document_type} • {(doc.file_size / 1024).toFixed(1)} KB • 
                      {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              ))}
              {documents.length === 0 && (
                <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
                  No documents yet. Upload your CV or other documents above.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ai-profile' && (
          <div data-testid="ai-profile-content">
            {aiProfileData.is_stale && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded" data-testid="ai-profile-stale-banner">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">⚠️</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900">Your AI profile is out of date</h3>
                    <p className="text-sm text-amber-800 mt-1">
                      You've changed your employment history since the active profile (v{aiProfileData.approved_version?.version}) was approved.
                      Employers viewing your vault are still seeing the older version. Regenerate and approve a fresh version to keep your profile accurate.
                    </p>
                    <button
                      onClick={handleGenerateAIProfile}
                      disabled={generatingProfile}
                      className="mt-3 px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50"
                      data-testid="ai-stale-regenerate-button"
                    >
                      {generatingProfile ? 'Generating...' : 'Regenerate Now'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-5 mb-6">
              <div className="flex items-start gap-3">
                <div className="text-3xl">🛡️</div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Candidate-Approved AI Profile</h2>
                  <p className="text-sm text-gray-700">
                    Generate a privacy-preserving professional summary from your employment history.
                    <strong> No personal identifiers (name, email, phone) are sent to the AI</strong> — only your job roles and achievements.
                    You review and approve every version before it can be shared. Every employer view is logged with the exact version they saw.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow mb-6">
              <h3 className="text-lg font-semibold mb-4">Generate New Version</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Target Role (optional)</label>
                  <input
                    type="text"
                    value={aiTargetRole}
                    onChange={(e) => setAiTargetRole(e.target.value)}
                    placeholder="e.g. VP, Credit Risk"
                    className="w-full px-4 py-2 border rounded-lg"
                    data-testid="ai-target-role-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Your Notes (optional)</label>
                  <textarea
                    value={aiNotes}
                    onChange={(e) => setAiNotes(e.target.value)}
                    placeholder="Anything you want the AI to emphasise — career goals, key transitions, specializations..."
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg"
                    data-testid="ai-notes-input"
                  />
                </div>
                <button
                  onClick={handleGenerateAIProfile}
                  disabled={generatingProfile || employmentRecords.length === 0}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="ai-generate-button"
                >
                  {generatingProfile ? 'Generating...' : 'Generate AI Profile'}
                </button>
                {employmentRecords.length === 0 && (
                  <p className="text-xs text-amber-700">Add at least one employment record first.</p>
                )}
              </div>
            </div>

            {aiProfileData.approved_version && (
              <div className="bg-green-50 border-2 border-green-300 p-6 rounded-lg shadow mb-6" data-testid="ai-profile-approved">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-600 text-white">
                      ACTIVE — v{aiProfileData.approved_version.version}
                    </span>
                    <p className="text-xs text-gray-600 mt-2">
                      Approved: {new Date(aiProfileData.approved_version.approved_at).toLocaleString()}
                    </p>
                    <p className="text-xs font-mono text-gray-500 mt-1">
                      Hash: {aiProfileData.approved_version.content_hash.substring(0, 16)}...
                    </p>
                  </div>
                  <button
                    onClick={() => handleUnapproveProfile(aiProfileData.approved_version.id)}
                    className="px-3 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600"
                    data-testid="ai-unapprove-button"
                  >
                    Pull Back to Draft
                  </button>
                </div>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800 bg-white p-4 rounded border">
                  {aiProfileData.approved_version.content}
                </div>
              </div>
            )}

            <div className="bg-white p-6 rounded-lg shadow mb-6">
              <h3 className="text-lg font-semibold mb-4">Version History ({aiProfileData.versions.length})</h3>
              {aiProfileData.versions.length === 0 ? (
                <p className="text-sm text-gray-500">No versions yet. Generate your first AI profile above.</p>
              ) : (
                <div className="space-y-3">
                  {aiProfileData.versions.map((profile) => (
                    <div key={profile.id} className="border rounded-lg p-4" data-testid={`ai-profile-version-${profile.version}`}>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">v{profile.version}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            profile.status === 'approved' ? 'bg-green-100 text-green-800' :
                            profile.status === 'superseded' ? 'bg-gray-100 text-gray-600' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {profile.status}
                          </span>
                          {profile.target_role && (
                            <span className="text-xs text-gray-500">target: {profile.target_role}</span>
                          )}
                          <span className="text-xs text-gray-500">
                            {new Date(profile.generated_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setExpandedProfileId(expandedProfileId === profile.id ? null : profile.id)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                          >
                            {expandedProfileId === profile.id ? 'Hide' : 'View'}
                          </button>
                          {profile.status !== 'approved' && (
                            <button
                              onClick={() => handleApproveProfile(profile.id)}
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              data-testid={`ai-approve-button-${profile.version}`}
                            >
                              Approve
                            </button>
                          )}
                          {profile.status !== 'approved' && (
                            <button
                              onClick={() => handleDeleteProfile(profile.id)}
                              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                      {expandedProfileId === profile.id && (
                        <div className="mt-2 bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap text-gray-800 border" data-testid={`ai-profile-content-${profile.version}`}>
                          {profile.content}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Employer View Audit Trail</h3>
                <button
                  onClick={loadAIProfileViews}
                  className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                >
                  Refresh
                </button>
              </div>
              {aiProfileViews.length === 0 ? (
                <p className="text-sm text-gray-500">No employer has viewed your AI profile yet.</p>
              ) : (
                <div className="space-y-2" data-testid="ai-profile-audit-trail">
                  {aiProfileViews.map((view) => (
                    <div key={view.id} className="flex justify-between items-center bg-gray-50 p-3 rounded text-sm">
                      <div>
                        <p className="font-semibold">{view.viewer_name}</p>
                        <p className="text-xs text-gray-600">{view.viewer_email}</p>
                        <p className="text-xs text-gray-500 font-mono">Hash: {view.content_hash.substring(0, 16)}...</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">v{view.profile_version}</span>
                        <p className="text-xs text-gray-500 mt-1">{new Date(view.viewed_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {showRequestReference && !selectedEmployment && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded m-4">
          Please select an employment record first.
        </div>
      )}

      {activeTab === 'compliance-pack' && (
        <div data-testid="compliance-pack-content">
          <CandidateCompliancePack />
        </div>
      )}
    </div>
  );
}

function CredibilityPanel({ credibility, onClose, onUpdate, companyNumber }) {
  const [verifying, setVerifying] = useState(null);
  const [emailVerifyData, setEmailVerifyData] = useState({ work_email: '', company_number: companyNumber || '', code: '', step: 'enter' });

  const handleSendOtp = async () => {
    setVerifying('email');
    try {
      const { data } = await axios.post(`${API}/reference/verify-email-send`, {
        work_email: emailVerifyData.work_email,
        company_number: emailVerifyData.company_number
      });
      alert(`Code sent! ${data.note_mvp || ''}`);
      setEmailVerifyData({...emailVerifyData, step: 'code'});
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setVerifying(null);
    }
  };

  const handleVerifyOtp = async () => {
    setVerifying('email');
    try {
      await axios.post(`${API}/reference/verify-email-confirm`, {
        work_email: emailVerifyData.work_email,
        code: emailVerifyData.code
      });
      alert('Email verified successfully! Credibility upgraded.');
      setEmailVerifyData({ work_email: '', company_number: companyNumber || '', code: '', step: 'enter' });
      onUpdate();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setVerifying(null);
    }
  };

  const handleCheckOfficer = async () => {
    if (!emailVerifyData.company_number) {
      alert('Enter a company number first');
      return;
    }
    setVerifying('officer');
    try {
      const { data } = await axios.post(`${API}/reference/check-officer-status?company_number=${emailVerifyData.company_number}`);
      alert(data.message);
      if (data.is_officer) onUpdate();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setVerifying(null);
    }
  };

  const [leiCompanyName, setLeiCompanyName] = useState('');
  const handleLeiLookup = async () => {
    if (!leiCompanyName) {
      alert('Enter your company name');
      return;
    }
    setVerifying('lei');
    try {
      const { data } = await axios.get(`${API}/reference/lei-lookup?company_name=${encodeURIComponent(leiCompanyName)}`);
      if (data.found) {
        alert(`✅ LEI Verified!\n\nLEI: ${data.lei}\nLegal Name: ${data.legal_name}\nStatus: ${data.registration_status}\n\n+15 credibility points awarded`);
        onUpdate();
      } else {
        alert(data.message || 'No LEI found for this company name');
      }
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setVerifying(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-2 border-blue-200" data-testid="credibility-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">🎯 Build Your Reference Credibility</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        The more verified you are, the more weight your references carry. Banking and financial sectors trust higher credibility scores.
      </p>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span>Progress to next level</span>
          {credibility.next_level && (
            <span className="font-semibold">{credibility.points_to_next_level} pts to {credibility.next_level}</span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all"
            style={{ width: `${credibility.score}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0</span>
          <span>Bronze (21)</span>
          <span>Silver (41)</span>
          <span>Gold (61)</span>
          <span>Platinum (81)</span>
          <span>100</span>
        </div>
      </div>

      {/* Verification Factors */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {Object.entries(credibility.factors).map(([key, factor]) => (
          <div
            key={key}
            className={`p-3 rounded-lg border ${factor.earned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
            data-testid={`credibility-factor-${key}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{factor.earned ? '✅' : '⭕'}</span>
                  <span className="font-semibold text-sm">{factor.label}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{factor.description}</p>
                {!factor.earned && factor.upgrade_hint && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">💡 {factor.upgrade_hint}</p>
                )}
              </div>
              <span className={`text-sm font-bold ${factor.earned ? 'text-green-700' : 'text-gray-400'}`}>
                +{factor.max}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-3">⚡ Quick Upgrades</h3>
        
        {/* Email Verification */}
        {!credibility.factors.email_verified?.earned && (
          <div className="bg-blue-50 p-4 rounded-lg mb-3">
            <p className="font-semibold mb-2">📧 Verify Work Email (+10 to +25 pts)</p>
            <p className="text-xs text-gray-600 mb-3">Get +15 bonus if domain matches your company on Companies House</p>
            
            {emailVerifyData.step === 'enter' && (
              <div className="space-y-2">
                <input
                  type="email"
                  placeholder="your.name@company.co.uk"
                  value={emailVerifyData.work_email}
                  onChange={(e) => setEmailVerifyData({...emailVerifyData, work_email: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  data-testid="work-email-input"
                />
                <input
                  type="text"
                  placeholder="Companies House Number (e.g. 00048839)"
                  value={emailVerifyData.company_number}
                  onChange={(e) => setEmailVerifyData({...emailVerifyData, company_number: e.target.value})}
                  className="w-full px-3 py-2 border rounded font-mono"
                  data-testid="credibility-company-number"
                />
                <button
                  onClick={handleSendOtp}
                  disabled={verifying === 'email'}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  data-testid="send-otp-button"
                >
                  {verifying === 'email' ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            )}

            {emailVerifyData.step === 'code' && (
              <div className="space-y-2">
                <p className="text-sm">Code sent to <strong>{emailVerifyData.work_email}</strong></p>
                <input
                  type="text"
                  placeholder="6-digit code"
                  maxLength="6"
                  value={emailVerifyData.code}
                  onChange={(e) => setEmailVerifyData({...emailVerifyData, code: e.target.value})}
                  className="w-full px-3 py-2 border rounded font-mono text-lg"
                  data-testid="otp-code-input"
                />
                <button
                  onClick={handleVerifyOtp}
                  disabled={verifying === 'email'}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  data-testid="verify-otp-button"
                >
                  {verifying === 'email' ? 'Verifying...' : 'Verify Code'}
                </button>
                <button
                  onClick={() => setEmailVerifyData({...emailVerifyData, step: 'enter', code: ''})}
                  className="ml-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        )}

        {/* Officer Check */}
        {!credibility.factors.is_officer?.earned && (
          <div className="bg-purple-50 p-4 rounded-lg mb-3">
            <p className="font-semibold mb-2">🎖️ Check Companies House Officer Status (+25 pts)</p>
            <p className="text-xs text-gray-600 mb-2">
              Are you listed as a director or officer? Check using your registered name.
            </p>
            <input
              type="text"
              placeholder="Company number"
              value={emailVerifyData.company_number}
              onChange={(e) => setEmailVerifyData({...emailVerifyData, company_number: e.target.value})}
              className="w-full px-3 py-2 border rounded font-mono mb-2"
            />
            <button
              onClick={handleCheckOfficer}
              disabled={verifying === 'officer'}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              data-testid="check-officer-button"
            >
              {verifying === 'officer' ? 'Checking...' : 'Check Officer Status'}
            </button>
          </div>
        )}

        {/* GLEIF LEI Lookup */}
        {!credibility.factors.lei_verified?.earned && (
          <div className="bg-green-50 p-4 rounded-lg mb-3">
            <p className="font-semibold mb-2">🌐 GLEIF LEI Verification (+15 pts)</p>
            <p className="text-xs text-gray-600 mb-2">
              Free global Legal Entity Identifier lookup. Banking-grade verification accepted worldwide.
            </p>
            <input
              type="text"
              placeholder="Your company's legal name (e.g., BARCLAYS PLC)"
              value={leiCompanyName}
              onChange={(e) => setLeiCompanyName(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-2"
              data-testid="lei-company-input"
            />
            <button
              onClick={handleLeiLookup}
              disabled={verifying === 'lei'}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              data-testid="lei-lookup-button"
            >
              {verifying === 'lei' ? 'Looking up...' : '🔍 Look Up LEI'}
            </button>
          </div>
        )}

        {!credibility.factors.mfa_enabled?.earned && (
          <div className="bg-yellow-50 p-4 rounded-lg mb-3">
            <p className="font-semibold mb-2">🔐 Enable 2FA (+10 pts)</p>
            <p className="text-xs text-gray-600">Use an authenticator app to add another layer of security to your account.</p>
            <p className="text-xs text-blue-600 mt-1">Configure 2FA in your account settings (coming soon to UI)</p>
          </div>
        )}
      </div>
    </div>
  );
}

function EmployerDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [profile, setProfile] = useState(null);
  const [verifyNumber, setVerifyNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reference, setReference] = useState({
    rating: 5,
    relationship: '',
    comments: '',
    would_rehire: true
  });
  const navigate = useNavigate();

  const loadPendingRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employer/references/pending`);
      setPendingRequests(data);
    } catch (err) {
      logger.error('Failed to load pending requests', err);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employer/profile`);
      setProfile(data);
    } catch (err) {
      logger.error('Failed to load profile', err);
    }
  }, []);

  const [credibility, setCredibility] = useState(null);
  const [showCredibilityPanel, setShowCredibilityPanel] = useState(false);

  const loadCredibility = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reference/credibility-status`);
      setCredibility(data);
    } catch (err) {
      logger.error('Failed to load credibility', err);
    }
  }, []);

  useEffect(() => {
    loadPendingRequests();
    loadProfile();
    loadCredibility();
  }, [loadPendingRequests, loadProfile, loadCredibility]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verifyNumber) {
      alert('Please enter a company number');
      return;
    }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/employer/verify`, { company_number: verifyNumber });
      loadProfile();
      alert(data.message || 'Company verified successfully!');
      setSearchResults([]);
      setSearchQuery('');
      setVerifyNumber('');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 404) {
        alert(`Verification failed: ${detail}\n\nTip: Use the search above to find your company by name — it will auto-fill the correct number. Real numbers are 8 characters (e.g., 00617987 for HSBC HOLDINGS PLC, 00048839 for BARCLAYS PLC).`);
      } else {
        alert('Verification failed: ' + detail);
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      alert('Enter at least 2 characters');
      return;
    }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/employer/search-companies?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(data.items || []);
      if (data.items.length === 0) {
        alert('No companies found. Try a different search term.');
      }
    } catch (err) {
      alert('Search failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSearching(false);
    }
  };

  const handleSubmitReference = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employer/references/${selectedRequest.id}/submit`, reference);
      setSelectedRequest(null);
      setReference({
        rating: 5,
        relationship: '',
        comments: '',
        would_rehire: true
      });
      loadPendingRequests();
      alert('Reference submitted successfully!');
    } catch (err) {
      alert('Failed to submit reference');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm" data-testid="employer-dashboard">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="text-xl font-bold text-blue-900">Vault Hire</div>
          <div className="flex gap-4 items-center">
            <MessageBell onOpen={() => navigate('/messages')} />
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              data-testid="employer-logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <AnnualUpsellBanner />
        <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
          <h1 className="text-3xl font-bold">Welcome, {user?.full_name}</h1>
          {credibility && (
            <button
              onClick={() => setShowCredibilityPanel(!showCredibilityPanel)}
              className="bg-white rounded-lg shadow-lg p-4 border-l-4 border-blue-500 text-left hover:shadow-xl transition"
              data-testid="credibility-widget"
            >
              <div className="flex items-center gap-4">
                <div className="text-4xl">{credibility.level_emoji}</div>
                <div>
                  <div className="text-2xl font-bold">{credibility.score}/100</div>
                  <div className="font-semibold text-blue-700">{credibility.level} Verifier</div>
                  <div className="text-xs text-gray-500">{credibility.level_description}</div>
                  {credibility.next_level && (
                    <div className="text-xs text-green-600 mt-1">
                      +{credibility.points_to_next_level} pts to {credibility.next_level} →
                    </div>
                  )}
                </div>
              </div>
            </button>
          )}
        </div>

        {showCredibilityPanel && credibility && (
          <CredibilityPanel
            credibility={credibility}
            onClose={() => setShowCredibilityPanel(false)}
            onUpdate={loadCredibility}
            companyNumber={profile?.company_number}
          />
        )}

        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'pending' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="pending-references-tab"
          >
            Pending References
          </button>
          <button
            onClick={() => setActiveTab('verify')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'verify' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="verify-company-tab"
          >
            Company Verification
          </button>
          <button
            onClick={() => setActiveTab('compliance-packs')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'compliance-packs' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="employer-compliance-packs-tab"
          >
            🛡️ Compliance Packs
          </button>
          <button
            onClick={() => navigate('/cockpit')}
            className="px-6 py-2 rounded-lg transition bg-indigo-600 text-white hover:bg-indigo-700"
            data-testid="employer-cockpit-link"
          >
            🎯 Vault Hub
          </button>
          <button
            onClick={() => navigate('/pm')}
            className="px-6 py-2 rounded-lg transition bg-blue-600 text-white hover:bg-blue-700"
            data-testid="employer-pm-link"
          >
            📋 My Implementation
          </button>
          <a
            href="/jobs"
            target="_blank"
            rel="noreferrer"
            className="px-6 py-2 rounded-lg transition bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            data-testid="employer-careers-link"
          >
            🌐 Public Careers Page
          </a>
        </div>

        {activeTab === 'pending' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">Pending Reference Requests</h2>

            {selectedRequest && (
              <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h3 className="text-xl font-semibold mb-4">Submit Reference</h3>
                <p className="mb-4 text-sm text-gray-600">
                  For: {selectedRequest.candidate_name} - {selectedRequest.job_title} at {selectedRequest.company_name}
                </p>
                <form onSubmit={handleSubmitReference} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Rating (1-5)</label>
                    <input
                      type="number"
                      value={reference.rating}
                      onChange={(e) => setReference({...reference, rating: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border rounded-lg"
                      min="1"
                      max="5"
                      required
                      data-testid="reference-rating"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Relationship</label>
                    <input
                      type="text"
                      value={reference.relationship}
                      onChange={(e) => setReference({...reference, relationship: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="e.g., Direct Manager"
                      required
                      data-testid="reference-relationship"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Comments</label>
                    <textarea
                      value={reference.comments}
                      onChange={(e) => setReference({...reference, comments: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows="4"
                      required
                      data-testid="reference-comments"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={reference.would_rehire}
                        onChange={(e) => setReference({...reference, would_rehire: e.target.checked})}
                      />
                      <span className="text-sm font-medium">Would rehire</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      data-testid="reference-submit-button"
                    >
                      Submit Reference
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedRequest(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <div key={request.id} className="bg-white p-6 rounded-lg shadow" data-testid="reference-request-employer">
                  {request.was_anonymous && request.anonymity_disclosure && (
                    <div className="bg-indigo-50 border-l-4 border-indigo-500 p-3 mb-3 rounded text-sm" data-testid="anonymity-disclosure-banner">
                      <p className="font-semibold text-indigo-900">🔒 Anonymous request — name revealed on sign-in</p>
                      <p className="text-indigo-900 mt-1 text-xs leading-relaxed">{request.anonymity_disclosure}</p>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">{request.candidate_name}</h3>
                      <p className="text-gray-600">{request.job_title}</p>
                      <p className="text-sm text-gray-500">{request.company_name}</p>
                      {request.message && (
                        <p className="text-sm mt-2 italic">"{request.message}"</p>
                      )}
                      <p className="text-sm text-gray-500 mt-2">
                        Requested: {new Date(request.request_date).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      data-testid="provide-reference-button"
                    >
                      Provide Reference
                    </button>
                  </div>
                </div>
              ))}
              {pendingRequests.length === 0 && (
                <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
                  No pending reference requests.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'verify' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">Company Verification</h2>

            {profile && profile.verification_status === 'verified' ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">✓</span>
                    <h3 className="text-xl font-semibold text-green-900">Company Verified (Companies House UK)</h3>
                  </div>
                  {profile.companies_house_data && (
                    <div className="space-y-2 text-sm">
                      <p><strong>Company Name:</strong> {profile.companies_house_data.company_name}</p>
                      <p><strong>Company Number:</strong> {profile.companies_house_data.company_number}</p>
                      <p><strong>Status:</strong> {profile.companies_house_data.company_status}</p>
                      <p><strong>Type:</strong> {profile.companies_house_data.company_type}</p>
                    </div>
                  )}
                </div>

                {/* GLEIF LEI Badge */}
                {profile.companies_house_data?.lei_lookup?.found && (
                  <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg" data-testid="lei-badge">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">🌐</span>
                      <h3 className="text-xl font-semibold text-blue-900">GLEIF LEI Verified (Global Standard)</h3>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><strong>LEI:</strong> <code className="bg-white px-2 py-1 rounded">{profile.companies_house_data.lei_lookup.lei}</code></p>
                      <p><strong>Legal Name:</strong> {profile.companies_house_data.lei_lookup.legal_name}</p>
                      <p><strong>Status:</strong> {profile.companies_house_data.lei_lookup.registration_status}</p>
                      <p><strong>Authority:</strong> {profile.companies_house_data.lei_lookup.registration_authority}</p>
                      {profile.companies_house_data.lei_lookup.next_renewal_date && (
                        <p className="text-xs text-gray-500">Next renewal: {new Date(profile.companies_house_data.lei_lookup.next_renewal_date).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Sanctions Screening */}
                {profile.companies_house_data?.sanctions_screening && (
                  <div className={`p-6 rounded-lg border ${
                    profile.companies_house_data.sanctions_screening.status === 'clear' ? 'bg-green-50 border-green-200' :
                    profile.companies_house_data.sanctions_screening.status === 'matches_found' ? 'bg-red-50 border-red-300' :
                    'bg-yellow-50 border-yellow-200'
                  }`} data-testid="sanctions-badge">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">
                        {profile.companies_house_data.sanctions_screening.status === 'clear' ? '🛡️' :
                         profile.companies_house_data.sanctions_screening.status === 'matches_found' ? '🚨' : '⏸️'}
                      </span>
                      <h3 className="text-xl font-semibold">
                        {profile.companies_house_data.sanctions_screening.status === 'clear' ? 'Sanctions Cleared' :
                         profile.companies_house_data.sanctions_screening.status === 'matches_found' ? 'Sanctions Matches - Review Required' :
                         'Sanctions Screening Pending Setup'}
                      </h3>
                    </div>
                    <p className="text-sm">{profile.companies_house_data.sanctions_screening.message}</p>
                    {profile.companies_house_data.sanctions_screening.status === 'clear' && (
                      <p className="text-xs text-green-700 mt-2">Screened against UK FCA, US OFAC, EU & UN sanctions lists via OpenSanctions</p>
                    )}
                    {profile.companies_house_data.sanctions_screening.status === 'pending_setup' && (
                      <p className="text-xs text-gray-600 mt-2">Get a free API key at opensanctions.org/api/ and add to backend .env as OPENSANCTIONS_API_KEY</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">🇬🇧 Verify via Companies House (UK)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Live verification against the official UK Companies House register. This ensures candidates can trust your reference.
                </p>

                {/* Search by Name */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold mb-2">🔍 Don't know your company number?</h4>
                  <p className="text-xs text-gray-600 mb-3">Search Companies House by company name</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1 px-4 py-2 border rounded-lg"
                      placeholder="e.g., Barclays"
                      data-testid="company-search-input"
                    />
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={searching}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      data-testid="company-search-button"
                    >
                      {searching ? 'Searching...' : 'Search'}
                    </button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
                      {searchResults.map((c) => (
                        <div
                          key={c.company_number}
                          className={`p-3 rounded border ${c.company_status === 'active' ? 'bg-white border-green-200 hover:border-green-400 cursor-pointer' : 'bg-gray-100 border-gray-200 opacity-60'}`}
                          onClick={() => c.company_status === 'active' && setVerifyNumber(c.company_number)}
                          data-testid="search-result-item"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-sm">{c.title}</p>
                              <p className="text-xs text-gray-600">
                                #{c.company_number} • {c.company_type} • <span className={c.company_status === 'active' ? 'text-green-600' : 'text-red-600'}>{c.company_status}</span>
                              </p>
                              {c.address_snippet && <p className="text-xs text-gray-500 mt-1">{c.address_snippet}</p>}
                            </div>
                            {c.company_status === 'active' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVerifyNumber(c.company_number);
                                }}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                Select
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Manual entry / Verify */}
                <form onSubmit={handleVerify} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Company Number</label>
                    <input
                      type="text"
                      value={verifyNumber}
                      onChange={(e) => setVerifyNumber(e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg font-mono"
                      placeholder="e.g., 00048839"
                      required
                      data-testid="company-number-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">8-digit UK company number (e.g., 00048839 for Barclays PLC)</p>
                  </div>
                  <button
                    type="submit"
                    disabled={verifying}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    data-testid="verify-company-button"
                  >
                    {verifying ? 'Verifying with Companies House...' : '✓ Verify Company'}
                  </button>
                </form>
                <p className="text-xs text-gray-500 mt-4">
                  🔒 Real-time verification via official UK Companies House API
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'compliance-packs' && (
          <div data-testid="employer-compliance-packs-content">
            <h2 className="text-2xl font-semibold mb-4">Compliance Packs</h2>
            <EmployerCompliancePacks />
          </div>
        )}
      </div>
    </div>
  );
}

function SecureVaultView() {
  const { token_id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [vaultInfo, setVaultInfo] = useState(null);
  const [vaultData, setVaultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const loadVaultInfo = useCallback(async () => {
    try {
      // First check if vault exists (public info)
      const { data } = await axios.get(`${API}/public/vault-info/${token_id}`);
      setVaultInfo(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load vault');
    } finally {
      setLoading(false);
    }
  }, [token_id]);

  const loadVaultData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vault/view/${token_id}`);
      setVaultData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load vault data');
    }
  }, [token_id]);

  useEffect(() => {
    if (!authLoading) {
      loadVaultInfo();
    }
  }, [authLoading, loadVaultInfo]);

  useEffect(() => {
    if (user && vaultInfo && !vaultData) {
      loadVaultData();
    }
  }, [user, vaultInfo, vaultData, loadVaultData]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading vault...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded" data-testid="vault-error">
          {error}
        </div>
      </div>
    );
  }

  // Not authenticated - show login/register prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-4" data-testid="auth-required-title">Authentication Required</h2>
          <p className="text-gray-600 mb-2">
            You've been granted access to view
          </p>
          <p className="text-lg font-semibold mb-4">{vaultInfo?.candidate_name}'s Vault</p>
          <p className="text-sm text-gray-500 mb-6">
            For security and audit purposes, you must verify your identity before accessing this vault.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(`/login?redirect=/view/${token_id}`)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="vault-login-button"
            >
              Login
            </button>
            <button
              onClick={() => navigate(`/register?redirect=/view/${token_id}`)}
              className="px-6 py-2 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
              data-testid="vault-register-button"
            >
              Register
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-6">
            Link expires: {new Date(vaultInfo?.expires_at).toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  if (!vaultData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading vault data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8" data-testid="vault-view">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">{vaultData.candidate_name}'s Vault</h1>
              <p className="text-sm text-gray-500">
                Expires: {new Date(vaultData.expires_at).toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Viewing as:</p>
              <p className="text-sm font-semibold">{user.email}</p>
              <p className="text-xs text-gray-400">Access logged for audit</p>
            </div>
          </div>

          {vaultData.email && (
            <div className="mb-6 bg-blue-50 p-4 rounded">
              <h2 className="text-xl font-semibold mb-2">Contact Information</h2>
              <p><strong>Email:</strong> {vaultData.email}</p>
              {vaultData.phone && <p><strong>Phone:</strong> {vaultData.phone}</p>}
            </div>
          )}

          {vaultData.ai_profile && (
            <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 p-5 rounded-lg" data-testid="employer-ai-profile">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span>🛡️</span> Candidate-Approved AI Profile
                </h2>
                <div className="text-right">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-indigo-600 text-white">
                    v{vaultData.ai_profile.version}
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    Approved: {new Date(vaultData.ai_profile.approved_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {vaultData.ai_profile.ai_disclosure && (
                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900" data-testid="ai-disclosure-banner">
                  <p className="font-semibold flex items-center gap-1">
                    <span>🤖</span>
                    AI-generated content disclosure (EU AI Act Art. 50)
                  </p>
                  <p className="mt-1 leading-relaxed">{vaultData.ai_profile.ai_disclosure.notice}</p>
                  <p className="mt-1 opacity-75">Model: {vaultData.ai_profile.ai_disclosure.model}</p>
                </div>
              )}
              <div className="bg-white p-4 rounded border whitespace-pre-wrap text-sm text-gray-800 prose prose-sm max-w-none">
                {vaultData.ai_profile.content}
              </div>
              <p className="text-xs text-gray-500 mt-2 font-mono">
                Tamper-proof hash: {vaultData.ai_profile.content_hash.substring(0, 24)}...
              </p>
              <p className="text-xs text-gray-500 mt-1">
                This is a candidate-approved summary. Raw CV data is intentionally not shared to preserve candidate privacy.
              </p>
            </div>
          )}

          {vaultData.employment_history && vaultData.employment_history.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-4">Employment History</h2>
              <div className="space-y-4">
                {vaultData.employment_history.map((record) => (
                  <div key={record.id} className="border-l-4 border-blue-600 pl-4 py-2">
                    <h3 className="font-semibold">{record.job_title}</h3>
                    <p className="text-gray-600">{record.company_name}</p>
                    <p className="text-sm text-gray-500">
                      {record.start_date} - {record.current ? 'Present' : record.end_date}
                    </p>
                    {record.responsibilities && (
                      <p className="text-sm mt-2">{record.responsibilities}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {vaultData.documents && vaultData.documents.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-4">Documents</h2>
              <div className="space-y-2">
                {vaultData.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div>
                      <p className="font-semibold">{doc.filename}</p>
                      <p className="text-xs text-gray-500">{doc.document_type} • {(doc.file_size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={async () => {
                        const { data } = await axios.get(`${API}/vault/documents/${doc.id}/download`);
                        const link = document.createElement('a');
                        link.href = `data:${data.content_type};base64,${data.file_data}`;
                        link.download = data.filename;
                        link.click();
                      }}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 pt-4 border-t text-xs text-gray-500">
            🔒 This access has been logged. The candidate can see who viewed their vault.
          </div>
        </div>
      </div>
    </div>
  );
}

// Fix import for SecureVaultView

function ATSTemplatesView() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ats/templates`).then(r => setTemplates(r.data));
  }, []);

  const loadTemplateDetail = async (id) => {
    const { data } = await axios.get(`${API}/ats/templates/${id}`);
    setSelectedTemplate(data);
  };

  const handleExport = async (id) => {
    const { data } = await axios.get(`${API}/ats/templates/${id}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `template-${id.substring(0,8)}.json`;
    link.click();
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">Recruitment Stage Templates</h2>
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
        <p className="text-sm text-blue-900">
          <strong>🏛️ Hierarchical Templates:</strong> Global → Regional → Legal Entity → Role.
          Compliance officers can lock stages or allow flexibility per company needs.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {templates.map(t => (
          <div key={t.id} className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500" data-testid="template-card">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-bold">{t.name}</h3>
                <p className="text-xs text-gray-500">{t.region} • {t.regulatory_framework}</p>
              </div>
              {t.locked && <span title="Locked" className="text-yellow-600">🔒</span>}
            </div>
            <p className="text-sm text-gray-600 mb-3">{t.description}</p>
            <div className="text-xs text-gray-500 mb-3">
              <span className="font-semibold">{t.stages.length}</span> stages • Level: {t.level} • v{t.version}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => loadTemplateDetail(t.id)}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                data-testid="view-template-button"
              >
                View Stages
              </button>
              <button
                onClick={() => handleExport(t.id)}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                data-testid="export-template-button"
              >
                Export
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedTemplate && (
        <div className="bg-white p-6 rounded-lg shadow" data-testid="template-detail">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold">{selectedTemplate.name}</h3>
              <p className="text-sm text-gray-500">{selectedTemplate.description}</p>
            </div>
            <button onClick={() => setSelectedTemplate(null)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>

          <div className="space-y-3">
            {selectedTemplate.stages.map((stage) => (
              <div key={`${selectedTemplate.id}-${stage.order}`} className="p-3 border rounded">
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold">{stage.order}</span>
                    <h4 className="font-semibold">{stage.name}</h4>
                    {stage.lock_mandatory && <span title="Mandatory" className="text-red-600 text-xs">🔒 Mandatory</span>}
                    {stage.lock_order && <span title="Sequential" className="text-blue-600 text-xs">↓ Sequential</span>}
                    {!stage.allow_override && <span title="No Override" className="text-purple-600 text-xs">⛔ No Override</span>}
                  </div>
                  {stage.sla_days && <span className="text-xs text-gray-500">SLA: {stage.sla_days}d</span>}
                </div>
                <p className="text-xs text-gray-600 mb-1">{stage.description}</p>
                {stage.compliance_gates?.length > 0 && (
                  <div className="text-xs text-purple-700 mt-1">
                    <strong>Gates:</strong> {stage.compliance_gates.join(', ')}
                  </div>
                )}
                {stage.required_documents?.length > 0 && (
                  <div className="text-xs text-blue-700 mt-1">
                    <strong>Docs:</strong> {stage.required_documents.join(', ')}
                  </div>
                )}
                {stage.required_approvers?.length > 0 && (
                  <div className="text-xs text-green-700 mt-1">
                    <strong>Approvers:</strong> {stage.required_approvers.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RoadmapView() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', description: '', category: 'ATS', priority: 'medium', status: 'planned', effort_estimate: 'medium', notes: '' });

  const loadItems = useCallback(async () => {
    const { data } = await axios.get(`${API}/owner/roadmap`);
    setItems(data);
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleStatusChange = async (id, status) => {
    await axios.put(`${API}/owner/roadmap/${id}`, { status });
    loadItems();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/owner/roadmap`, newItem);
    setNewItem({ title: '', description: '', category: 'ATS', priority: 'medium', status: 'planned', effort_estimate: 'medium', notes: '' });
    setShowForm(false);
    loadItems();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await axios.delete(`${API}/owner/roadmap/${id}`);
    loadItems();
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter || i.category === filter);
  const stats = {
    planned: items.filter(i => i.status === 'planned').length,
    in_progress: items.filter(i => i.status === 'in_progress').length,
    completed: items.filter(i => i.status === 'completed').length,
    deferred: items.filter(i => i.status === 'deferred').length
  };

  const priorityColor = (p) => ({ critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-blue-100 text-blue-700', low: 'bg-gray-100 text-gray-600' })[p];
  const statusColor = (s) => ({ planned: 'bg-gray-100 text-gray-700', in_progress: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700', deferred: 'bg-purple-100 text-purple-700' })[s];

  return (
    <div data-testid="roadmap-view">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">🗺️ Product Roadmap</h2>
        <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="add-roadmap-button">
          {showForm ? 'Cancel' : '+ Add Item'}
        </button>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-white p-3 rounded-lg shadow text-center"><div className="text-2xl font-bold text-gray-600">{stats.planned}</div><div className="text-xs">Planned</div></div>
        <div className="bg-white p-3 rounded-lg shadow text-center"><div className="text-2xl font-bold text-yellow-600">{stats.in_progress}</div><div className="text-xs">In Progress</div></div>
        <div className="bg-white p-3 rounded-lg shadow text-center"><div className="text-2xl font-bold text-green-600">{stats.completed}</div><div className="text-xs">Completed</div></div>
        <div className="bg-white p-3 rounded-lg shadow text-center"><div className="text-2xl font-bold text-purple-600">{stats.deferred}</div><div className="text-xs">Deferred</div></div>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-white p-4 rounded-lg shadow mb-4 space-y-3" data-testid="roadmap-form">
          <input required placeholder="Title" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="roadmap-title-input" />
          <textarea placeholder="Description" value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} className="w-full px-3 py-2 border rounded" rows="2" />
          <div className="grid grid-cols-4 gap-3">
            <select value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} className="px-3 py-2 border rounded">
              {['ATS','Wellness','AI','Integration','Compliance','UI','Other'].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select value={newItem.priority} onChange={e => setNewItem({...newItem, priority: e.target.value})} className="px-3 py-2 border rounded">
              {['critical','high','medium','low'].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select value={newItem.status} onChange={e => setNewItem({...newItem, status: e.target.value})} className="px-3 py-2 border rounded">
              {['planned','in_progress','completed','deferred'].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select value={newItem.effort_estimate} onChange={e => setNewItem({...newItem, effort_estimate: e.target.value})} className="px-3 py-2 border rounded">
              {['small','medium','large','xl'].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded">Add to Roadmap</button>
        </form>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {['all','planned','in_progress','completed','deferred','ATS','Wellness','AI','Integration','Compliance','UI'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-sm rounded ${filter === f ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map(item => (
          <div key={item.id} className="bg-white p-4 rounded-lg shadow flex items-start gap-4" data-testid="roadmap-item">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{item.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded ${priorityColor(item.priority)}`}>{item.priority}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${statusColor(item.status)}`}>{item.status.replace('_',' ')}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">{item.category}</span>
                {item.effort_estimate && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">📊 {item.effort_estimate}</span>}
              </div>
              {item.description && <p className="text-sm text-gray-600">{item.description}</p>}
              {item.notes && <p className="text-xs text-gray-500 italic mt-1">📝 {item.notes}</p>}
            </div>
            <div className="flex flex-col gap-1">
              <select value={item.status} onChange={e => handleStatusChange(item.id, e.target.value)} className="text-xs px-2 py-1 border rounded" data-testid="roadmap-status-select">
                {['planned','in_progress','completed','deferred'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
              </select>
              <button onClick={() => handleDelete(item.id)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200">Delete</button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">No items match this filter.</div>
        )}
      </div>
    </div>
  );
}

function AdminDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (err) {
      logger.error('Failed to load users', err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch (err) {
      logger.error('Failed to load stats', err);
    }
  }, []);

  const loadAuditLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/audit-logs`);
      setAuditLogs(data);
    } catch (err) {
      logger.error('Failed to load audit logs', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadStats();
    loadAuditLogs();
  }, [loadUsers, loadStats, loadAuditLogs]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Reset password for ${resetEmail}?`)) return;
    try {
      await axios.post(`${API}/admin/users/reset-password`, {
        user_email: resetEmail,
        new_password: resetPassword
      });
      alert(`Password reset successfully. New password: ${resetPassword}`);
      setResetEmail('');
      setResetPassword('');
    } catch (err) {
      alert('Reset failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleAccount = async (userId, currentlyDisabled) => {
    if (!window.confirm(`${currentlyDisabled ? 'Enable' : 'Disable'} this account?`)) return;
    try {
      await axios.post(`${API}/admin/users/toggle-status`, {
        user_id: userId,
        disabled: !currentlyDisabled
      });
      loadUsers();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-slate-900 shadow-sm" data-testid="admin-dashboard">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="text-xl font-bold text-white">Vault Hire <span className="text-sm font-normal text-red-400">ADMIN</span></div>
          <div className="flex gap-4 items-center">
            <MessageBell onOpen={() => navigate('/messages')} />
            <span className="text-sm text-gray-300">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              data-testid="admin-logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-2">System Administration</h1>
        <p className="text-sm text-gray-600 mb-6">⚠️ Admins cannot view user data, only system-level metadata and operations</p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-3xl font-bold text-blue-600">{stats.total_users}</div>
              <div className="text-sm text-gray-600">Total Users</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-3xl font-bold text-green-600">{stats.candidates}</div>
              <div className="text-sm text-gray-600">Candidates</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-3xl font-bold text-purple-600">{stats.employers}</div>
              <div className="text-sm text-gray-600">Employers ({stats.verified_employers} verified)</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-3xl font-bold text-orange-600">{stats.active_access_links}</div>
              <div className="text-sm text-gray-600">Active Access Links</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'users' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-users-tab"
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('reset')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'reset' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-reset-tab"
          >
            Password Reset
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'audit' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-audit-tab"
          >
            Audit Logs
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'templates' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-templates-tab"
          >
            ATS Templates
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'rules' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-rules-tab"
          >
            Rules Engine
          </button>
          <button
            onClick={() => setActiveTab('sandbox')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'sandbox' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-sandbox-tab"
          >
            🧪 Compliance Sandbox
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'integrations' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-integrations-tab"
          >
            Integrations
          </button>
          <button
            onClick={() => setActiveTab('walkthrough')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'walkthrough' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-walkthrough-tab"
          >
            Walkthrough access
          </button>
          <button
            onClick={() => setActiveTab('ai-governance')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'ai-governance' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-ai-governance-tab"
          >
            AI Governance
          </button>
          <button
            onClick={() => setActiveTab('scheduler')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'scheduler' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-scheduler-tab"
          >
            📅 Scheduler
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'jobs' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-jobs-tab"
          >
            🌐 Publish Jobs
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'analytics' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-analytics-tab"
          >
            📊 Analytics
          </button>
          <button
            onClick={() => setActiveTab('investors')}
            className={`px-6 py-2 rounded-lg transition ${activeTab === 'investors' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            data-testid="admin-investors-tab"
          >
            🔐 Investors
          </button>
          <button
            onClick={() => navigate('/admin/cockpit')}
            className="px-6 py-2 rounded-lg transition bg-white text-gray-700 hover:bg-gray-100"
            data-testid="admin-cockpit-link"
          >
            Vault Hub →
          </button>
          <button
            onClick={() => navigate('/admin/compliance')}
            className="px-6 py-2 rounded-lg transition bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="admin-compliance-portal-link"
          >
            🛡️ Compliance Portal →
          </button>
          <button
            onClick={() => navigate('/admin/pm')}
            className="px-6 py-2 rounded-lg transition bg-blue-600 text-white hover:bg-blue-700"
            data-testid="admin-pm-portal-link"
          >
            📋 Implementation PM →
          </button>
          <button
            onClick={() => navigate('/admin/ai-controls')}
            className="px-6 py-2 rounded-lg transition bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]"
            data-testid="admin-ai-controls-link"
          >
            AI Controls →
          </button>
          <RecruiterHQAiToggle />
          {user?.role === 'owner' && (
            <button
              onClick={() => setActiveTab('roadmap')}
              className={`px-6 py-2 rounded-lg transition ${activeTab === 'roadmap' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}
              data-testid="owner-roadmap-tab"
            >
              🗺️ Roadmap (Owner)
            </button>
          )}
        </div>

        {activeTab === 'templates' && <ATSTemplatesView />}
        {activeTab === 'rules' && <RulesEngineView />}
        {activeTab === 'sandbox' && <ComplianceSandboxView />}
        {activeTab === 'integrations' && <IntegrationsView />}
        {activeTab === 'walkthrough' && (
          <div className="space-y-6">
            <RecentWalkthroughViewers />
            <WalkthroughInbox />
          </div>
        )}
        {activeTab === 'ai-governance' && <AIGovernanceView />}
        {activeTab === 'scheduler' && <InterviewSchedulerView />}
        {activeTab === 'jobs' && <PublishJobsView />}
        {activeTab === 'analytics' && <AnalyticsDashboard />}
        {activeTab === 'investors' && <InvestorAdmin />}
        {activeTab === 'roadmap' && user?.role === 'owner' && <RoadmapView />}

        {activeTab === 'users' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">Users (Metadata Only)</h2>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t" data-testid="admin-user-row">
                      <td className="px-4 py-3 text-sm">{u.email}</td>
                      <td className="px-4 py-3 text-sm">{u.full_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          u.role === 'admin' ? 'bg-red-100 text-red-700' :
                          u.role === 'candidate' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{u.role}</span>
                        {u.company_verified && <span className="ml-1 text-green-600 text-xs">✓ Verified</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm">
                        {u.disabled ? 
                          <span className="text-red-600">Disabled</span> : 
                          <span className="text-green-600">Active</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {u.role !== 'admin' && (
                          <button
                            onClick={() => handleToggleAccount(u.id, u.disabled)}
                            className={`px-3 py-1 text-xs rounded ${
                              u.disabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                            } text-white`}
                            data-testid="admin-toggle-button"
                          >
                            {u.disabled ? 'Enable' : 'Disable'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'reset' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">Password Reset</h2>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Emergency Password Reset</strong>: Use only when a user cannot use the self-service forgot password flow. All resets are logged.
              </p>
            </div>
            <form onSubmit={handleResetPassword} className="bg-white p-6 rounded-lg shadow max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">User Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  data-testid="admin-reset-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">New Password</label>
                <input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  minLength="8"
                  data-testid="admin-reset-password"
                />
                <p className="text-xs text-gray-500 mt-1">Visible to admin for communication. User should change on next login.</p>
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                data-testid="admin-reset-submit"
              >
                Reset Password
              </button>
            </form>
          </div>
        )}

        {activeTab === 'audit' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">System Audit Logs</h2>
            <p className="text-sm text-gray-600 mb-4">Last 500 actions across the platform</p>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">User ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-t" data-testid="audit-log-row">
                      <td className="px-4 py-3 text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm font-mono">{log.action}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{log.user_id?.substring(0, 8)}...</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{log.ip_address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HiringManagerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow px-6 py-3 flex justify-between items-center">
        <h1 className="text-xl font-bold text-indigo-700">Vault Hire <span className="text-sm font-normal text-gray-500 ml-2">Hiring Manager</span></h1>
        <div className="flex items-center gap-3">
          <MessageBell onOpen={() => navigate('/messages')} />
          <span className="text-sm text-gray-600">{user?.email}</span>
          <button onClick={() => { logout(); navigate('/'); }} className="px-3 py-1.5 bg-red-500 text-white text-sm rounded">Logout</button>
        </div>
      </nav>
      <HiringManagerPortal />
    </div>
  );
}

function CockpitPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow px-6 py-3 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-indigo-700">Vault Hire</h1>
          <button onClick={() => navigate(user?.role === 'employer' ? '/employer/dashboard' : '/admin/dashboard')} className="text-sm text-gray-600 hover:text-indigo-600">← Back</button>
        </div>
        <div className="flex items-center gap-3">
          <RecruiterCandidateActions compact />
          <MessageBell onOpen={() => navigate('/messages')} />
          <span className="text-sm text-gray-600">{user?.email}</span>
          <button onClick={() => { logout(); navigate('/'); }} className="px-3 py-1.5 bg-red-500 text-white text-sm rounded">Logout</button>
        </div>
      </nav>
      <RecruiterCockpit />
    </div>
  );
}

/**
 * Shelled — wraps a page in <AppShell> using the authenticated user.
 * Used to roll out the new global chrome onto existing routes without
 * needing to rewrite each page's internal layout.
 */
function Shelled({ children, role }) {
  const { user, logout, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--vh-text-muted)]">Loading…</div>;
  if (!user || user === false) return <Navigate to="/login" />;
  if (role && user.role !== role) {
    if (!(role === 'admin' && user.role === 'owner')) {
      return <Navigate to="/" />;
    }
  }
  return <AppShellRuntime user={user} onLogout={logout}>{children}</AppShellRuntime>;
}


function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/magic-link" element={<MagicLinkConsumePage />} />
          <Route path="/security/passkeys" element={
            <Shelled>
              <PasskeyManagerPage />
            </Shelled>
          } />
          <Route path="/candidate/vault" element={
            <ProtectedRoute>
              <VaultPage />
            </ProtectedRoute>
          } />
          <Route path="/candidate/dashboard" element={
            <ProtectedRoute role="candidate">
              <CandidateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/employer/dashboard" element={
            <ProtectedRoute role="employer">
              <RecruiterWidgetDashboard />
            </ProtectedRoute>
          } />
          <Route path="/employer/classic" element={
            <ProtectedRoute role="employer">
              <EmployerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/dashboard" element={
            <Shelled role="admin">
              <AdminDashboard />
            </Shelled>
          } />
          <Route path="/view/:token_id" element={<SecureVaultView />} />
          <Route path="/cockpit" element={
            <Shelled role="employer">
              <RecruiterHQ />
            </Shelled>
          } />
          <Route path="/recruiter" element={
            <Shelled role="employer">
              <RecruiterHQ />
            </Shelled>
          } />
          <Route path="/cockpit/legacy" element={
            <ProtectedRoute role="employer">
              <CockpitPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/cockpit" element={
            <ProtectedRoute role="admin">
              <CockpitPage />
            </ProtectedRoute>
          } />
          <Route path="/jobs" element={<PublicJobsBoard />} />
          <Route path="/jobs/:job_id" element={<PublicJobDetail />} />
          <Route path="/hiring-manager" element={
            <Shelled role="hiring_manager">
              <HiringManagerPage />
            </Shelled>
          } />
          <Route path="/messages" element={
            <Shelled>
              <MessageBoard />
            </Shelled>
          } />
          <Route path="/pricing" element={<PricingRoute />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/trust/data-minimisation" element={<TrustDataMinimisationPage />} />
          <Route path="/compliance" element={<ComplianceCustomerPortal />} />
          <Route path="/admin/compliance" element={
            <Shelled role="admin">
              <ComplianceAdminPortal />
            </Shelled>
          } />
          <Route path="/admin/pm" element={
            <Shelled role="admin">
              <PMProjectList adminMode />
            </Shelled>
          } />
          <Route path="/admin/pm/inbox" element={
            <Shelled role="admin">
              <PMInbox />
            </Shelled>
          } />
          <Route path="/admin/pm/:projectId" element={
            <Shelled role="admin">
              <PMProjectDetail adminMode />
            </Shelled>
          } />
          <Route path="/settings/ai-preferences" element={
            <Shelled>
              <AIPreferencesPage />
            </Shelled>
          } />
          <Route path="/admin/ai-controls" element={
            <Shelled role="admin">
              <AdminAIControlsPage />
            </Shelled>
          } />
          <Route path="/compliance-hub" element={
            <Shelled>
              <ComplianceHubPage />
            </Shelled>
          } />
          <Route path="/admin/dpa-submissions" element={
            <Shelled role="admin">
              <AdminDPAQueuePage />
            </Shelled>
          } />
          <Route path="/pm" element={
            <ProtectedRoute>
              <PMProjectList adminMode={false} />
            </ProtectedRoute>
          } />
          <Route path="/pm/:projectId" element={
            <ProtectedRoute>
              <PMProjectDetail adminMode={false} />
            </ProtectedRoute>
          } />
          <Route path="/companies" element={<CompaniesRoute />} />
          <Route path="/investors" element={<InvestorRoom />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function PricingRoute() {
  const { user } = useAuth();
  return <PricingPage user={user} />;
}

function CompaniesRoute() {
  const { user } = useAuth();
  return <CompaniesDirectory user={user} />;
}

export default App;
